<template>
  <b-container class="mw-100 mt-3 mb-2">
    <b-card no-body>
      <validation-observer ref="mainForm" v-slot="{ invalid }">
        <b-tabs card v-model="currentTab" content-class="mb-0" ref="tabContainer">
          <b-tab :title="`${$tc('message.identityData', 1)}`">
            <b-container class="m-0 p-0 mw-100 mb-2">
              <transition :name="transitionName">
                <b-row v-show="tabs.mainBody.index === currentTab && tabs.mainBody.active">
                  <b-col>
                    <b-container class="mw-100 mt-2">
                      <b-row>
                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.numberPlate', 1).toLowerCase()"
                                               :rules="{ required: true, regex: /^[a-zA-Z0-9-]+$/, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="`${$tc('message.numberPlate', 1)} (*)`"
                                          label-for="input-licence-plate">
                              <b-form-input id="input-licence-plate"
                                            v-model="mainBody.plateNumber"
                                            autocomplete="off" type="text"
                                            :placeholder="`${$tc('message.example', 1)}: EF589DA`"
                                            :state="itemChanged ? valid : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.brand', 1).toLowerCase()"
                                               :rules="{ max: 50 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$t('message.brand')"
                                          label-for="input-brand">
                              <b-form-input id="input-brand"
                                            v-model="mainBody.type.brand"
                                            autocomplete="off" type="text"
                                            :placeholder="`${$tc('message.example', 1)}: Fiat`"
                                            :state="itemChanged && !valid ? false : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.model', 1).toLowerCase()"
                                               :rules="{ max: 50 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.model', 1)"
                                          label-for="input-template">
                              <b-form-input id="input-template"
                                            v-model="mainBody.type.model"
                                            autocomplete="off" type="text"
                                            :placeholder="`${$tc('message.example', 1)}: Z150`"
                                            :state="itemChanged && !valid ? false : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="`${$tc('message.date', 1)} ${$tc('message.matriculation', 1).toLowerCase()}`"
                                        label-for="input-matriculation">
                            <b-input-group>
                              <flat-pickr id="input-matriculation"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          :readonly="false"
                                          v-model="mainBody.registrationDate"
                                          :config="optionRegistrationDate"
                                          @on-change="registrationDateChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.registrationDate = null"
                                          :disabled="!mainBody.registrationDate">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <b-row>
                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="$tc('message.type', 1)"
                                        label-for="input-classification">
                            <model-list-select id="input-classification"
                                               option-text="text"
                                               option-value="value"
                                               :placeholder="classificationOptions && classificationOptions.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                               v-model="mainBody.type.classification"
                                               :list="classificationOptions">
                            </model-list-select>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.chassisNumber', 1).toLowerCase()"
                                               :rules="{ max: 50 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.chassisNumber', 1)"
                                          label-for="input-chassis-number">
                              <b-form-input id="input-chassis-number"
                                            v-model="mainBody.chassisNumber"
                                            autocomplete="off" type="text"
                                            :placeholder="`${$tc('message.example', 1)}: ZFA3340000P492325`"
                                            :state="itemChanged && !valid ? false : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="2">
                          <validation-provider :name="$tc('message.payload', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 30}"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="`${$tc('message.payload', 1)}`"
                                          label-for="input-payload">
                              <b-input-group append="kg">
                                <b-form-input id="input-payload"
                                              v-model="mainBody.type.payload"
                                              autocomplete="off" step="1" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 500`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.type.payload, true)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="6" md="3" lg="4" xl="2">
                          <validation-provider :name="$tc('message.engineDisplacement', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 30 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.engineDisplacement', 1)"
                                          label-for="input-engine-displacement">
                              <b-form-input id="input-engine-displacement"
                                            v-model="mainBody.type.engineDisplacement"
                                            autocomplete="off" min="0"
                                            :placeholder="`${$tc('message.example', 1)}: 1600`"
                                            :state="itemChanged && !valid ? false : null"
                                            @keypress="isNumber($event, mainBody.type.engineDisplacement, true)">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="6" md="3" lg="4" xl="2">
                          <validation-provider :name="$tc('message.power', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 30 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.power', 1)"
                                          label-for="input-power">
                              <b-input-group append="kW">
                                <b-form-input id="input-KW"
                                              v-model="mainBody.type.kw"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 85`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.type.kw, true)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                      </b-row>

                      <b-row>
                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="`${$tc('message.type', 1)} ${$tc('message.refueling', 1).toLowerCase()}`"
                                        label-for="input-fuel">
                            <model-list-select id="input-fuel"
                                               option-text="text"
                                               option-value="value"
                                               :placeholder="fuels && fuels.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                               v-model="mainBody.type.fuelType"
                                               :list="fuels">
                            </model-list-select>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="2">
                          <validation-provider :name="$tc('message.seats', 1).toLowerCase()"
                                               :rules="{ min_value: 1, max: 30 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.seats', 1)"
                                          label-for="input-seats">
                              <b-form-input id="input-seats"
                                            v-model="mainBody.type.seats"
                                            autocomplete="off" min="1"
                                            :placeholder="`${$tc('message.example', 1)}: 9`"
                                            :state="itemChanged && !valid ? false : null"
                                            @keypress="isNumber($event, mainBody.type.seats, true)">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.emissionStandard', 1).toLowerCase()"
                                               :rules="{ max: 50 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.emissionStandard', 1)"
                                          label-for="input-emission-standard">
                              <b-form-input id="input-emission-standard"
                                            v-model="mainBody.type.emissionStandard"
                                            autocomplete="off" type="text"
                                            :placeholder="`${$tc('message.example', 1)}: 6`"
                                            :state="itemChanged && !valid ? false : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                      </b-row>

                      <b-row>
                        <b-col cols="12" sm="6" md="3" lg="2" xl="2">
                          <b-form-group :label="$t('message.trailerHitch')"
                                        label-for="input-trailer-hitch">
                            <b-form-checkbox id="input-trailer-hitch"
                                             v-model="mainBody.type.trailerHitch">
                            </b-form-checkbox>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="6" md="3" lg="2" xl="2">
                          <b-form-group :label="$t('message.vehicleSetUp')"
                                        label-for="input-has-equipment">
                            <b-form-checkbox id="input-has-equipment"
                                             @change="checkEquipment"
                                             v-model="showEquipment">
                            </b-form-checkbox>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <transition name="scroll-fade">
                            <b-form-group :label="`${$tc('message.type', 1)} ${$t('message.vehicleSetUp').toLowerCase()}`"
                                          label-for="input-equipment" v-show="showEquipment">
                              <model-list-select id="input-equipment"
                                                 option-text="text"
                                                 option-value="value"
                                                 v-model="mainBody.type.equipment"
                                                 :list="equipmentOptions">
                              </model-list-select>
                            </b-form-group>
                          </transition>
                        </b-col>
                      </b-row>
                    </b-container>
                  </b-col>
                </b-row>
              </transition>
            </b-container>
          </b-tab>

          <b-tab :title="`${$t('message.extraData')}`">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.details.index === currentTab && tabs.details.active">
                <b-row>
                  <b-col>
                    <b-form-group :label="`${$tc('message.vehicle', 1)} ${$tc('message.inOperation', 1).toLowerCase()}`"
                                  label-for="input-disable">
                      <model-list-select id="input-disabled-option"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="disabledOptions && disabledOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="inOperation"
                                         @input="checkDisablingDate"
                                         :list="disabledOptions">
                      </model-list-select>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="6" md="4" lg="4" xl="2">
                    <transition :name="transitionName">
                      <b-form-group :label="`${$t('message.date')} ${$tc('message.disabled', 1).toLowerCase()}`"
                                    label-for="input-disable-date" v-show="!inOperation">
                        <b-input-group>
                          <flat-pickr id="input-disable-date"
                                      :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                      class="form-control"
                                      :disabled="!!mainBody.imei"
                                      v-model="mainBody.disablingDate"
                                      :config="disablingDateConfig">
                          </flat-pickr>
                          <div class="input-group-append">
                            <b-button @click="mainBody.disablingDate = null"
                                      :disabled="!mainBody.disablingDate">
                              <font-awesome-icon icon="trash"/>
                            </b-button>
                          </div>
                        </b-input-group>
                      </b-form-group>
                    </transition>
                  </b-col>

                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">

                    <b-form-group :label="`${$tc('message.status', 1)} ${$tc('message.vehicle', 1).toLowerCase()}`"
                                  label-for="input-states">
                      <model-list-select id="input-states"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="states && states.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="mainBody.state"
                                         :list="states">
                      </model-list-select>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="4" xl="4">
                    <b-form-group :label="$tc('message.behalfOf', 1)"
                                  label-for="input-ownUse">
                      <model-list-select id="input-ownUse"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="useCase && useCase.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="mainBody.ownUse"
                                         :list="useCase">
                      </model-list-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                    <b-form-group :label="$tc('message.property', 1)"
                                  label-for="input-property">
                      <model-list-select id="input-property"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="properties && properties.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="editedOwnership.owner"
                                         :list="properties">
                      </model-list-select>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">

                    <b-form-group :label="$t('message.userCompany')"
                                  label-for="input-society">
                      <model-list-select id="input-society"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="companies && companies.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="mainBody.userCompany"
                                         :list="companies">
                      </model-list-select>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">

                    <b-form-group :label="`${$tc('message.sector', 1)} ${$tc('message.activity', 1).toLowerCase()}`"
                                  label-for="input-sector">
                      <model-list-select id="input-sector"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="sectors && sectors.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="mainBody.activitySector"
                                         :list="sectors">
                      </model-list-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="m-1 mb-4">
                  <b-col class="border-bottom border-c-green">
                    <label class="tiny-label c-green"> {{$tc('message.measures', 1)}}</label>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" sm="12" md="12" lg="6" xl="6">
                    <validation-provider :name="$tc('message.costPerKm', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 10 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="$t('message.costPerKm')"
                                    label-for="input-km-euro">
                        <b-input-group append="km/€">
                          <b-form-input id="input-km-euro"
                                        v-model="mainBody.kmCost"
                                        autocomplete="off" min="0" step="0.01"
                                        :placeholder="`${$tc('message.example', 1)}: 12`"
                                        :state="itemChanged && !valid ? false : null"
                                        @keypress="isNumber($event, mainBody.kmCost)">
                          </b-form-input>
                        </b-input-group>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="6" xl="6">
                    <validation-provider :name="$tc('message.avgConsumption', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 10 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$t('message.avgConsumption')}`"
                                    label-for="input-avg-consumption">
                        <b-input-group append="km/l">
                          <b-form-input id="input-avg-consumption"
                                        v-model="mainBody.avgConsumption"
                                        autocomplete="off" min="0" step="0.01"
                                        :placeholder="`${$tc('message.example', 1)}: 9`"
                                        :state="itemChanged && !valid ? false : null"
                                        @keypress="isNumber($event, mainBody.avgConsumption)">
                          </b-form-input>
                        </b-input-group>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="6" xl="4">
                    <validation-provider :name="$tc('message.length', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 10 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$tc('message.length', 1)}`"
                                    label-for="input-length">
                        <b-input-group append="cm">
                          <b-form-input id="input-length"
                                        v-model="mainBody.type.length"
                                        autocomplete="off" min="0"
                                        :placeholder="`${$tc('message.example', 1)}: 420`"
                                        :state="itemChanged && !valid ? false : null"
                                        @keypress="isNumber($event, mainBody.type.length, true)">
                          </b-form-input>
                        </b-input-group>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="6" xl="4">
                    <validation-provider :name="$tc('message.width', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 10 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$t('message.width')}`"
                                    label-for="input-width">
                        <b-input-group append="cm">
                          <b-form-input id="input-width"
                                        v-model="mainBody.type.width"
                                        autocomplete="off" min="0"
                                        :placeholder="`${$tc('message.example', 1)}: 180`"
                                        :state="itemChanged && !valid ? false : null"
                                        @keypress="isNumber($event, mainBody.type.width, true)">
                          </b-form-input>
                        </b-input-group>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="6" xl="4">
                    <validation-provider :name="$tc('message.maxWeight', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 6 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$t('message.maxWeight')}`"
                                    label-for="input-maxWeight">
                        <b-input-group append="Kg">
                          <b-form-input id="input-maxWeight"
                                        v-model="mainBody.type.traction"
                                        autocomplete="off" min="0"
                                        :placeholder="`${$tc('message.example', 1)}: 7500`"
                                        :state="itemChanged && !valid ? false : null"
                                        @keypress="isNumber($event, mainBody.type.traction)">
                          </b-form-input>
                        </b-input-group>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" sm="12" md="12" lg="12" xl="6">
                    <validation-provider :name="$tc('message.nowCheckedOnVehicle', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 15 }"
                                         v-slot="{ valid, errors }">
                    <b-form-group :label="`km (${$t('message.nowCheckedOnVehicle')})`"
                                  label-for="input-now-km">
                      <b-input-group append="km">
                        <b-form-input id="input-now-km"
                                      v-model="editedMeasures.km"
                                      autocomplete="off" min="0"
                                      :placeholder="`${$tc('message.example', 1)}: 150000`"
                                      :state="measuresHasErrors() !== null ? !measuresHasErrors() : null"
                                      @keypress="isNumber($event, editedMeasures.km, true)">
                        </b-form-input>
                      </b-input-group>

                      <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                    </b-form-group>
                    </validation-provider>
                    <div v-if="measuresHasErrors()" class="my-invalid-feedback">
                      <h6>{{$t('message.error')}} Km ({{$t('message.nowCheckedOnVehicle')}})
                        {{$tc('message.required', 1).toLowerCase()}}
                      </h6>
                    </div>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="12" xl="6">
                    <b-form-group :label="`${$tc('message.date', 1)} ${$t('message.survey').toLowerCase()}`"
                                  label-for="input-km-check">
                      <b-input-group>
                        <flat-pickr id="input-km-check"
                                    :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                    class="form-control"
                                    v-model="editedMeasures.date"
                                    :config="lastMeasureOption">
                        </flat-pickr>
                        <div class="input-group-append">
                          <b-button @click="editedMeasures.date = null"
                                    :disabled="!editedMeasures.date">
                            <font-awesome-icon icon="trash"/>
                          </b-button>
                        </div>
                      </b-input-group>
                    </b-form-group>
                    <div v-if="measuresHasErrors(true)" class="my-invalid-feedback">
                      <h6>{{$tc('message.date', 1)}} {{$tc('message.survey', 1).toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                    </div>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="12" xl="6">
                    <b-form-group :label="`km (${$t('message.automaticCheck').toLowerCase()})`"
                                  v-if="mainBody.estimatedKm">
                      <b-input-group>
                        <h5 class="c-green mt-2">{{mainBody.estimatedKm}} </h5>
                      </b-input-group>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="12" xl="6">
                    <b-form-group :label="`${$tc('message.date', 1)} ${$tc('message.update', 1).toLowerCase()} km`"
                                  v-if="mainBody.estimatedKm">
                      <b-input-group>
                        <h5 class="c-green mt-2" v-if="mainBody.estimatedKmTs">
                          {{DateTime.fromMillis(mainBody.estimatedKmTs * 1000).toFormat('dd-MM-yyyy')}}
                          {{DateTime.fromMillis(mainBody.estimatedKmTs * 1000).toFormat('hh:mm:ss')}}
                        </h5>

                        <h5 v-else>
                          - - - - -
                        </h5>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" sm="12" md="12" lg="12" xl="12">
                    <validation-provider :name="$tc('message.description', 1).toLowerCase()"
                                         :rules="{ max: 250 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$t('message.description')}`"
                                    label-for="input-description">
                        <b-form-input id="input-description"
                                      v-model="mainBody.description"
                                      autocomplete="off"
                                      type="text"
                                      :state="itemChanged && !valid ? false : null">
                        </b-form-input>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12">
                    <div>
                      <font-awesome-icon icon="info-circle" class="c-green"/>
                      {{$tc('info.updateVehicleAutomaticDate')}}
                    </div>
                  </b-col>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <b-tab :title="`${$t('message.vehicleUsage')}`"
                 :title-item-class="!mainBody.registrationDate ? 'disabled' : ''"
                 :disabled="!mainBody.registrationDate">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.vehicleUsage.index === currentTab && tabs.vehicleUsage.active">
                <b-row>
                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                    <b-form-group :label="$t('message.vehicleUsage')"
                                  label-for="input-use">
                      <model-list-select id="input-use"
                                         :isDisabled="invalid"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="ownershipType && ownershipType.length
                                             !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="ownershipTypeSelected"
                                         :list="ownershipType">
                      </model-list-select>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="12" xl="12" class="border-top m-2 mb-4 mt-3"/>

                  <b-container class="mw-100">
                    <transition name="scroll-fade"> <!-- Rental -->
                      <b-row v-show="ownershipTypeSelected === 'rental' || ownershipTypeSelected === 'loan'">
                        <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                          <validation-provider :name="$tc('message.society', 1).toLowerCase()"
                                               :rules="{ max: 50 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.society', 1)"
                                          label-for="input-rental-company">
                              <b-form-input id="input-rental-company"
                                            v-model="mainBody.ownership.rental.company"
                                            autocomplete="off"
                                            type="text"
                                            :placeholder="`${$tc('message.example', 1)}: ABCD.srl`"
                                            :state="itemChanged && !valid ? false : null">
                              </b-form-input>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                          <b-form-group :label="`${$t('message.date')} ${$t('message.start').toLowerCase()} (*)`"
                                        label-for="input-rental-startDate">
                            <b-input-group>
                              <flat-pickr id="input-rental-startDate"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          v-model="mainBody.ownership.rental.startDate" :config="rentalOptionDateStart"
                                          @on-change="rentalOptionDateStartChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.ownership.rental.startDate = null"
                                          :disabled="!mainBody.ownership.rental.startDate">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                          <b-form-group :label="`${$t('message.date')} ${$t('message.end').toLowerCase()} (*)`"
                                        label-for="input-rental-endDate">
                            <b-input-group>
                              <flat-pickr id="input-rental-endDate"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          v-model="mainBody.ownership.rental.endDate" :config="rentalOptionDateEnd"
                                          @on-change="rentalOptionDateEndChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.ownership.rental.endDate = null"
                                          :disabled="!mainBody.ownership.rental.endDate">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="12" lg="12" xl="12"> <!-- EMPTY COL --></b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.price', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$tc('message.price', 1)"
                                          label-for="input-rental-price">
                              <b-input-group append="€">
                                <b-form-input id="input-rental-price"
                                              v-model="mainBody.ownership.rental.price"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.rental.price)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$tc('message.society', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$t('message.redemptionPrice')"
                                          label-for="input-rental-redemptionPrice">
                              <b-input-group append="€">
                                <b-form-input id="input-rental-redemptionPrice"
                                              v-model="mainBody.ownership.rental.redemptionPrice"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.rental.redemptionPrice)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="2">
                          <validation-provider :name="$tc('message.relief', 1).toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$t('message.relief')"
                                          label-for="input-rental-relief">
                              <b-input-group append="€">
                                <b-form-input id="input-rental-relief"
                                              v-model="mainBody.ownership.rental.relief"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.rental.relief)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="2">
                          <validation-provider :name="'km / ' + $tc('message.year').toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="'km / ' + $tc('message.year')"
                                          label-for="input-rental-kmYear">
                              <b-input-group append="km">
                                <b-form-input id="input-rental-kmYear"
                                              v-model="mainBody.ownership.rental.kmYear"
                                              autocomplete="off" type="number" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              v-validate="'min_value:0'"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.rental.kmYear, true)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="2">
                          <validation-provider :name="'km / ' + $tc('message.max', 2).toLowerCase()"
                                               :rules="{ min_value: 0 , max: 15}"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="'km ' + $tc('message.max', 2).toLowerCase()"
                                          label-for="input-rental-maxKm">
                              <b-input-group append="km">
                                <b-form-input id="input-rental-maxKm"
                                              v-model="mainBody.ownership.rental.maxKm"
                                              autocomplete="off" type="number" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.rental.maxKm, true)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="12" lg="12" xl="12"> <!-- EMPTY COL --></b-col>

                        <b-col cols="12" sm="12" md="6" lg="2" xl="2">
                          <b-form-group :label="$t('message.loanerVehicle')"
                                        label-for="input-rental-loanerVehicle">
                            <b-form-checkbox id="input-rental-loanerVehicle"
                                             v-model="mainBody.ownership.rental.loanerVehicle">
                            </b-form-checkbox>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="2" xl="2">
                          <b-form-group :label="$t('message.towTruck')"
                                        label-for="input-rental-towTruck">
                            <b-form-checkbox id="input-rental-towTruck"
                                             v-model="mainBody.ownership.rental.towTruck">
                            </b-form-checkbox>
                          </b-form-group>
                        </b-col>
                      </b-row>
                    </transition>

                    <transition name="scroll-fade"> <!-- Leasing-->
                      <b-row v-show="ownershipTypeSelected === 'leasing'">
                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="`${$t('message.date')} ${$t('message.start').toLowerCase()} (*)`"
                                        label-for="input-leasing-start-date">
                            <b-input-group>
                              <flat-pickr id="input-leasing-start-date"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          v-model="mainBody.ownership.leasing.startDate"
                                          :config="leasingOptionDateStart"
                                          @on-change="leasingOptionDateStartChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.ownership.leasing.startDate = null"
                                          :disabled="!mainBody.ownership.leasing.startDate">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="`${$t('message.date')} ${$t('message.end').toLowerCase()} (*)`"
                                        label-for="input-leasing-end-date">
                            <b-input-group>
                              <flat-pickr id="input-leasing-end-date"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          v-model="mainBody.ownership.leasing.endDate"
                                          :config="leasingOptionDateEnd"
                                          @on-change="leasingOptionDateEndChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.ownership.leasing.endDate = null"
                                          :disabled="!mainBody.ownership.leasing.endDate">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$t('message.redemptionPrice').toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="$t('message.redemptionPrice')"
                                          label-for="input-leasing-price">
                              <b-input-group append="€">
                                <b-form-input id="input-leasing-price"
                                              v-model="mainBody.ownership.leasing.redemptionPrice"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 5000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.leasing.redemptionPrice)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                      </b-row>
                    </transition>

                    <transition name="scroll-fade"> <!-- Property -->
                      <b-row v-show="ownershipTypeSelected === 'property'">
                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <b-form-group :label="`${$t('message.date')} ${$t('message.purchase').toLowerCase()}`"
                                        label-for="input-purchase-date">
                            <b-input-group>
                              <flat-pickr id="input-purchase-date"
                                          :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                          class="form-control"
                                          v-model="mainBody.ownership.purchase.date"
                                          :config="purchaseOptionDateStart"
                                          @on-change="purchaseOptionDateStartChanged">
                              </flat-pickr>
                              <div class="input-group-append">
                                <b-button @click="mainBody.ownership.purchase.date = null"
                                          :disabled="!mainBody.ownership.purchase.date">
                                  <font-awesome-icon icon="trash"/>
                                </b-button>
                              </div>
                            </b-input-group>
                          </b-form-group>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="$t('message.redemptionPrice').toLowerCase()"
                                               :rules="{ min_value: 0, max: 15 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="`${$tc('message.price', 1)} ${$t('message.purchase').toLowerCase()}`"
                                          label-for="input-purchase-price">
                              <b-input-group append="€">
                                <b-form-input id="input-purchase-price"
                                              v-model="mainBody.ownership.purchase.price"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 80`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.purchase.price)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>

                        <b-col cols="12" sm="12" md="6" lg="4" xl="3">
                          <validation-provider :name="`km ${$t('message.purchase').toLowerCase()}`"
                                               :rules="{ min_value: 0, max: 20 }"
                                               v-slot="{ valid, errors }">
                            <b-form-group :label="`km ${$t('message.purchase').toLowerCase()}`"
                                          label-for="input-purchase-km">
                              <b-input-group append="km">
                                <b-form-input id="input-purchase-km"
                                              v-model="mainBody.ownership.purchase.km"
                                              autocomplete="off" min="0"
                                              :placeholder="`${$tc('message.example', 1)}: 150000`"
                                              :state="itemChanged && !valid ? false : null"
                                              @keypress="isNumber($event, mainBody.ownership.purchase.km, true)">
                                </b-form-input>
                              </b-input-group>

                              <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                      </b-row>
                    </transition>

                    <b-row v-if="ownershipTypeSelected === 'rental'
                        || ownershipTypeSelected === 'loan' || ownershipTypeSelected === 'leasing'">
                      <b-col cols="12" sm="12" md="6" lg="4" xl="3" class="text-center">
                        <label/>

                        <b-form-group class="mt-2">
                          <switch-button v-model="vehicleUsageNotification.active"
                                         :color="{checked: '#249a80', unchecked: '#dc3545'}"
                                         :sync="true"
                                         :width="200"
                                         id="reschedulr-event-executed"
                                         :height="32"
                                         @change="itemChanged = true"
                                         :font-size="15.5"
                                         :speed="500"
                                         :labels="true">
                            <template slot="checked">
                              {{$tc('message.notification', 1)}} {{$tc('message.enabled', 3).toLowerCase()}}
                            </template>

                            <template slot="unchecked">
                              {{$tc('message.notification', 1)}} {{$tc('message.disabled', 3).toLowerCase()}}
                            </template>
                          </switch-button>
                        </b-form-group>
                      </b-col>

                      <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                        <b-form-group :label="`${$tc('message.notificationGroup', 1)}`"
                                      label-for="input-notification">
                          <model-list-select id="input-notification"
                                             option-text="descr"
                                             option-value="id"
                                             :placeholder="allRecipientGroups && allRecipientGroups.length !== 0
                                                 ? $t('message.selectOption') : $t('message.noOption')"
                                             v-model="vehicleUsageNotification.notificationRecipientId"
                                             :list="allRecipientGroups">
                          </model-list-select>
                        </b-form-group>
                      </b-col>

                      <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                        <b-form-group :label="`${$tc('message.reminderGroup', 1)}`"
                                      label-for="input-reminder">
                          <model-list-select id="input-reminder"
                                             option-text="descr"
                                             option-value="id"
                                             :placeholder="allRecipientGroups && allRecipientGroups.length !== 0
                                                 ? $t('message.selectOption') : $t('message.noOption')"
                                             v-model="vehicleUsageNotification.reminderRecipientId"
                                             :list="allRecipientGroups">
                          </model-list-select>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-container>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <b-tab :title="$t('message.albo2')"
                 :disabled="!mainBody.registrationDate"
                 :title-item-class="!mainBody.registrationDate ? 'disabled' : ''">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.albo.index === currentTab && tabs.albo.active">
                <b-row>
                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                    <b-form-group :label="`${$t('message.date')}
                        ${$t('message.registration').toLowerCase()} ${$tc('message.albo', 1).toLowerCase()}`"
                                  label-for="input-albo-start">
                      <b-input-group>
                        <flat-pickr id="input-albo-start"
                                    :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                    class="form-control"
                                    v-model="mainBody.registry.registrationDate" :config="alboRegistrationOption">
                        </flat-pickr>
                        <div class="input-group-append">
                          <b-button @click="mainBody.registry.registrationDate = null"
                                    :disabled="!mainBody.registry.registrationDate">
                            <font-awesome-icon icon="trash"/>
                          </b-button>
                        </div>
                      </b-input-group>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="12" md="6" lg="4" xl="4">
                    <validation-provider :name="$t('message.commission').toLowerCase()"
                                         :rules="{ max: 50 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="$t('message.commission')"
                                    label-for="input-behalf-of">
                        <b-form-input id="input-behalf-of"
                                      v-model="mainBody.registry.behalfOf"
                                      autocomplete="off" type="text"
                                      :placeholder="`${$tc('message.example', 1)}: ABCD.srl`"
                                      :state="itemChanged && !valid ? false : null">
                        </b-form-input>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="12" md="12" lg="12" xl="12">
                    <b-form-group :label="`${$tc('message.code', 2)} ${$t('message.albo2')}`"
                                  label-for="input-albo">
                      <multi-select id="input-albo"
                                    :placeholder="alboCodes && alboCodes.length
                                        !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                    :selected-options="alboCodesSelected"
                                    :options="alboCodes"
                                    @select="alboSelection">
                      </multi-select>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <b-tab :title="$t('message.equipment')">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.equip.index === currentTab && tabs.equip.active">
                <b-row >
                  <b-col>
                    <b-form-group :label="`${$tc('message.chain', 2)}`"
                                  label-for="input-chains">
                      <b-form-checkbox id="input-chains"
                                       v-model="mainBody.extraEquipment.snowChains">
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>

                  <b-col>
                    <b-form-group :label="$t('message.cid')"
                                  label-for="input-cid">
                      <b-form-checkbox id="input-cid"
                                       v-model="mainBody.extraEquipment.cid">
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>

                  <b-col>
                    <b-form-group :label="$t('message.firstAidKit')"
                                  label-for="input-climate">
                      <b-form-checkbox id="input-climate"
                                       v-model="mainBody.extraEquipment.airConditioning">
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>

                  <b-col>
                    <b-form-group label="Gilet"
                                  label-for="input-gilet">
                      <b-form-checkbox id="input-gilet"
                                       v-model="mainBody.extraEquipment.gilet">
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12" sm="5" md="4" lg="3" xl="2">
                    <validation-provider :name="$tc('message.keyNumber', 1).toLowerCase()"
                                         :rules="{ min_value: 0, max: 15 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="`${$tc('message.keyNumber', 1)}`"
                                    label-for="input-keys">
                        <b-form-input id="input-keys"
                                      autocomplete="off" type="number" min="0"
                                      v-model="mainBody.extraEquipment.key"
                                      :placeholder="`${$tc('message.example', 1)}: 2`"
                                      :state="itemChanged && !valid ? false : null"
                                      @keypress="isNumber($event, mainBody.extraEquipment.key, true)">
                        </b-form-input>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col cols="12" sm="5" md="4" lg="3" xl="2">
                    <validation-provider :name="$tc('message.seasonalTires', 1).toLowerCase()"
                                         :rules="{ min: 0, max: 50 }"
                                         v-slot="{ valid, errors }">
                      <b-form-group :label="$t('message.seasonalTires')"
                                    label-for="input-season-tires">
                        <b-form-input id="input-season-tires"
                                      autocomplete="off" type="text"
                                      v-model="mainBody.extraEquipment.tireType">
                        </b-form-input>

                        <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <b-tab :title="`${$tc('message.service', 2)}`"
                 :disabled="!mainBody.plateNumber">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.services.index === currentTab && tabs.services.active">
                <b-row>
                  <b-col class="p-0">
                    <b-container class="mt-2 d-flex flex-column">
                      <b-row>
                        <standard-table :items="servicesItems"
                                        :fields="servicesFields"
                                        :id-table="'service-table'"
                                        ref="servicesTable"
                                        :search-id="'service-table-search'"

                                        :row-before-search="{cols: 12,sm: 12,md: 12,lg: 8,xl: 8,orderSm: 1,orderMd: 1,orderLg: 3,orderXl: 3}"
                                        :row-search="{cols: 12,sm: 12,md: 12,lg: 4,xl: 4,orderSm: 2,orderMd: 2,orderLg: 1,orderXl: 1}"

                                        :row-pagination="{cols: 12, sm: 12, md: 6, lg: 5, xl: 5}"
                                        :row-between-pagination="{cols: 12, sm: 12, md: 1, lg: 2, xl: 4}"
                                        :row-per-page="{cols: 12, sm: 12, md: 5, lg: 3, xl: 2}"
                                        :row-after-per-page="{cols: 12, sm: 12, md: 12, lg: 2, xl: 1}"

                                        @rowInfo="showServiceInfo($event.item)"
                                        @rowEdit="editServices($event.item)"
                                        @rowDelete="checkDeleteService($event.item)">

                          <b-container slot="row-before-search" class="mw-100 p-0">
                            <b-form>
                              <b-row align-h="end">
                                <b-col class="mb-3" cols="auto">
                                  <!-- config[crud], create - read - update - delete -->
                                  <b-button v-on:click="editServices()"
                                            class="b-c-blue float-right"
                                            id="add-service-button" v-if="config.c"> {{$t('message.add')}}
                                  </b-button>
                                </b-col>
                              </b-row>
                            </b-form>
                          </b-container>

                          <b-container slot="row-after-per-page" class="mw-100 p-0">
                            <b-row>
                              <b-col>
                                <b-button class="float-right mt-3 mt-md-0 mt-lg-0 mt-xl-0"
                                          @click="$refs.servicesTable.excel(`${$tc('message.service', 2)}_${mainBody.plateNumber}`)"
                                          id="excel-button">Excel
                                </b-button>
                              </b-col>
                            </b-row>
                          </b-container>
                        </standard-table>
                      </b-row>
                    </b-container>
                  </b-col>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <b-tab :title="`${$tc('message.insurance', 2)}`"
                 :disabled="!mainBody.plateNumber">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.insurance.index === currentTab && tabs.insurance.active">
                <b-row>
                  <b-col>
                    <b-container class="mt-2 d-flex flex-column">
                      <b-row>
                        <standard-table :items="insuranceItems"
                                        :fields="insuranceFields"
                                        :id-table="'insurance-table'"

                                        :row-before-search="{cols: 12,sm: 12,md: 12,lg: 8,xl: 8,orderSm: 1,orderMd: 1,orderLg: 3,orderXl: 3}"
                                        :row-search="{cols: 12,sm: 12,md: 12,lg: 4,xl: 4,orderSm: 2,orderMd: 2,orderLg: 1,orderXl: 1}"

                                        ellippsed
                                        @rowEdit="editInsurance($event.item)"
                                        @rowDelete="checkDeleteInsurance($event.item)">
                          <b-container slot="row-before-search" class="mw-100 p-0">
                            <b-form>
                              <b-row align-h="end">
                                <b-col class="mb-3" cols="auto">
                                  <!-- config[crud], create - read - update - delete -->
                                  <b-button v-on:click="editInsurance()"
                                            class="b-c-blue float-right"
                                            id="add-insurance-button" v-if="config.c"> {{$t('message.add')}}
                                  </b-button>
                                </b-col>
                              </b-row>
                            </b-form>
                          </b-container>
                        </standard-table>
                      </b-row>
                    </b-container>
                  </b-col>
                </b-row>
              </b-container>
            </transition>
          </b-tab>

          <!--<b-tab :title="$tc('message.attachment', 2)" disabled :title-item-class="'disabled'">
            <transition :name="transitionName">
              <b-container class="m-0 p-0 mw-100" v-show="tabs.docs.index === currentTab && tabs.docs.active">
                <file-uploader class="mt-2">
                </file-uploader>
              </b-container>
            </transition>
          </b-tab> -->

          <b-tab :title="$tc('message.obu', 2)" :disabled="!mainBody.plateNumber || !!mainBody.disablingDate" :title-item-class="'disabled'">
            <b-container class="m-0 p-0 mw-100">
              <transition :name="transitionName">
                <b-row v-if="tabs.obu.index === currentTab && tracker.serialNumber && tabs.obu.active">
                  <b-container>
                    <b-row align-h="center" class="mb-2">
                      <b-col cols="12" sm="6" md="4" lg="3" xl="2">
                        {{$t('message.serialNumber')}}: <h5 class="c-green">{{tracker.serialNumber}}</h5>
                      </b-col>

                      <b-col cols="12" sm="6" md="4" lg="3" xl="2">
                        {{$tc('message.model', 1)}}: <h5 class="c-green">{{tracker.type || $t('message.emptyValue')}}</h5>
                      </b-col>

                      <b-col cols="12" sm="6" md="4" lg="3" xl="2">
                        {{$tc('message.date', 1)}} {{$t('message.purchase').toLowerCase()}}: <h5 class="c-green">{{tracker.purchaseDate || $t('message.noDate')}}</h5>
                      </b-col>
                    </b-row>

                    <b-row align-h="center">
                      <!-- config[crud], create - read - update - delete -->
                      <b-button @click="openDeleteOBUModal()" class="b-c-green"
                                id="obu-disassociation"
                                v-if="vehicleId ? config.u && mainBody.imei : config.c && mainBody.imei"
                      >{{$t('message.dissociate')}} {{$t('message.obu')}}
                      </b-button>
                    </b-row>
                  </b-container>
                </b-row>
              </transition>

              <transition :name="transitionName">
                <b-container v-if="tabs.obu.index === currentTab && tabs.obu.active">
                  <b-row v-if="!tracker.serialNumber" align-h="center" class="mb-2">
                    <b-col cols="auto">
                      <h4 class="c-green mt-2">{{$t('message.noOBU')}}</h4>
                    </b-col>
                  </b-row>

                  <b-row align-h="center">
                    <!-- config[crud], create - read - update - delete -->
                    <b-button @click="openAddOBUModal()" class="b-c-green"
                              id="obu-association"
                              v-if="vehicleId ? config.u && !mainBody.imei : config.c && !mainBody.imei"
                    >{{$t('message.associate')}} {{$t('message.obu')}}
                    </b-button>
                  </b-row>
                </b-container>
              </transition>
            </b-container>
          </b-tab>
        </b-tabs>

        <b-container class="mw-100 mb-3 mt-2">
          <b-row v-show="currentTab !== tabs.services.index && currentTab !== tabs.insurance.index && currentTab !== tabs.obu.index">
            <b-col class=" justify-content-end d-flex">
              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2"
                        v-on:click="
                        currentTab !== tabs.vehicleUsage.index
                        ? saveVehicle(false) : addOwnershipTypeNotification()"
                        :disabled="checkMainSaveButton() || invalid"
                        v-if="vehicleId ? config.u : config.c"
                        id="save-vehicle">{{$t('message.save')}}
              </b-button>

              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2"
                        v-if="vehicleId ? config.u : config.c"
                        v-on:click="currentTab !== tabs.vehicleUsage.index
                        ? saveVehicle(true) : addOwnershipTypeNotification(true)"
                        :disabled="checkMainSaveButton() || invalid"
                        id="save-and-close-vehicle">{{$t('message.saveClose')}}
              </b-button>

              <b-button class="b-c-green" v-on:click="pushBack()" id="close-vehicle">{{$t('message.close')}}</b-button>
            </b-col>
          </b-row>
        </b-container>
      </validation-observer>
    </b-card>

    <b-modal ref="serviceInfo" id="serviceInfo"
             :title="`${$t('message.info')} ${$t('message.item').toLowerCase()}`">
      <b-container class="service-container">
        <b-row>
          <b-col cols="12" sm="12" md="12" lg="12" xl="12">
            <div class="d-flex">
              <h6><b>{{$tc('message.type', 1)}} {{$tc('message.service', 1).toLowerCase()}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.descr}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="6" xl="6">
            <div class="modal-body">
              <h6><b>PIN:</b></h6>
              <h6 class="ml-2">{{serviceInfo.pin || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="6" lg="6" xl="6">
            <div class="modal-body">
              <h6><b>{{$t('message.number')}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.number || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="12" lg="12" xl="12">
            <div class="d-flex">
              <h6><b>{{$t('message.expiration')}} {{$t('message.date').toLowerCase()}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.parsedExpDate || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row class="border-bottom border-c-green mb-1 mt-2">
          <b-col>
            <label class="c-green">
              {{$tc('message.notification', 1)}}
            </label>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.enabled', 2)}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.eventActive}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.notified', 1)}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.eventNotified}}</h6>
            </div>
          </b-col>

          <b-col cols="12" sm="12" md="4" lg="4" xl="4">
            <div class="d-flex">
              <h6><b>{{$tc('message.expired', 1)}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.eventExpired}}</h6>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12">
            <label class="c-green mt-2">
              {{$tc('message.notificationGroup', 1)}}:
              {{serviceInfo.notificationListName || $tc('message.emptyValue', 1)}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"/>

            <b-container class="max-height-200-px">
              <b-row v-for="item in serviceInfo.notificationList" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12">
            <label class="c-green mt-2">
              {{$tc('message.reminderGroup', 1)}}: {{serviceInfo.reminderListName || $tc('message.emptyValue', 1)}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"/>

            <b-container class="max-height-200-px">
              <b-row v-for="item in serviceInfo.reminderList" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <div class="modal-body mt-3">
              <h6><b>{{$tc('message.note', 2)}}:</b></h6>
              <h6 class="ml-2">{{serviceInfo.note || $tc('message.emptyValue', 1)}}</h6>
            </div>
          </b-col>
        </b-row>
      </b-container>

      <b-container slot="modal-footer" class="w-100">
        <b-row>
          <b-col>
            <b-button class="b-c-green ml-3 float-right mt-2" id="service-info-modal-close" v-on:click="$refs.serviceInfo.hide()">{{$t('message.close')}}</b-button>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
    <validation-observer ref="editServiceForm">
      <b-modal ref="editService" id="editService" :size="showServiceInfoGroups ? 'lg' : 'md'"
             :title="`${$t('message.add')} ${$t('message.item').toLowerCase()}`">
        <b-container>
          <b-row>
            <b-col cols="12" sm="12" md="12" :lg="showServiceInfoGroups ? 6 : 12" :xl="showServiceInfoGroups ? 6 : 12">
              <b-container class="p-0">
                <b-row>
                  <b-col>
                    <b-form-group :label="`${$tc('message.type', 1)} ${$tc('message.service', 1).toLowerCase()} (*)`"
                                  label-for="service-type">
                      <model-list-select id="service-type"
                                         option-text="text"
                                         option-value="value"
                                         :placeholder="typeOptions && typeOptions.length
                                                 !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                         v-model="serviceEdit.type"
                                         :list="typeOptions">
                      </model-list-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <validation-provider name="PIN"
                                         :rules="{ min: 0, max: 50 }"
                                         v-slot="{ changed, valid, errors }">
                      <b-form-group label="PIN"
                                    label-for="input-emission-standard">
                        <b-form-input id="input-emission-pin"
                                      v-model="serviceEdit.pin"
                                      autocomplete="off" type="text"
                                      :placeholder="`${$tc('message.example', 1)}: 6`"
                                      :state="changed && !valid ? false : null">
                        </b-form-input>

                        <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <validation-provider :name="$tc('message.number', 1).toLowerCase()"
                                         :rules="{ min: 0, max: 50 }"
                                         v-slot="{ changed, valid, errors }">
                      <b-form-group :label="$tc('message.number', 1)"
                                    label-for="input-number">
                        <b-form-input id="input-number"
                                      v-model="serviceEdit.number"
                                      autocomplete="off"
                                      :placeholder="`${$tc('message.example', 1)}: AK563BW7`"
                                      :state="changed && !valid ? false : null">
                        </b-form-input>

                        <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <b-form-group :label="`${$t('message.expiration')} ${$t('message.date').toLowerCase()} (*)`"
                                  label-for="input-expiration-date">
                      <b-input-group>
                        <flat-pickr id="input-expiration-date"
                                    :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                                    class="form-control"
                                    v-model="serviceEdit.expDate"
                                    :config="serviceSelectedOption">
                        </flat-pickr>
                        <div class="input-group-append">
                          <b-button @click="serviceEdit.expDate = null"
                                    :disabled="!serviceEdit.expDate">
                            <font-awesome-icon icon="trash"/>
                          </b-button>
                        </div>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row align-h="center">
                  <b-col cols="auto">
                    <b-form-group :label="$tc('message.notification', 1).toLowerCase()"
                                  label-for="input-executed">
                      <switch-button v-model="serviceEdit.eventActiveVal"
                                     :color="{checked: '#249a80', unchecked: '#dc3545'}"
                                     id="active-notification"
                                     :sync="true"
                                     :width="150"
                                     :height="32"
                                     :font-size="15.5"
                                     :speed="500"
                                     :labels="true">
                        <template slot="checked">
                          {{$tc('message.enabled', 3)}}
                        </template>

                        <template slot="unchecked">
                          {{$tc('message.disabled', 3)}}
                        </template>
                      </switch-button>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col>
                    <validation-provider :name="$tc('message.note', 2).toLowerCase()"
                                         :rules="{ min: 0, max: 250 }"
                                         v-slot="{ changed, valid, errors }">
                      <b-form-group :label="`${$tc('message.note', 2)}`"
                                    label-for="service-notes">
                        <b-form-textarea id="service-notes" type="text"
                                         v-model="serviceEdit.note"
                                         :state="changed && !valid ? false : null">
                        </b-form-textarea>

                        <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </b-container>
            </b-col>

            <b-col cols="12" sm="12" md="12" lg="6" xl="6">
              <transition name="slide-right">
                <b-container v-show="showServiceInfoGroups" class="p-0"
                             style="border-left: 1px solid #e9ecef !important; height: 100% !important;">
                  <b-container>
                    <b-row>
                      <b-col>
                        <h6>{{$t('info.willBeEnabledNotification')}}.</h6>
                      </b-col>
                    </b-row>

                    <b-row v-if="groupInfoItem">
                      <b-col cols="12 mt-3" v-if="groupInfoItem.notificationGroup">
                        <label class="c-green">
                          {{$tc('message.notificationGroup', 1)}}:
                          {{groupInfoItem.notificationGroup.descr}}
                        </label>

                        <b-row class="border-bottom border-c-green mb-2"/>

                        <b-container class="max-height-200-px">
                          <b-row v-for="item in groupInfoItem.notificationGroup.recipients" :key="item">
                            <div><h6>- {{item}}</h6></div>
                          </b-row>
                        </b-container>
                      </b-col>

                      <b-col cols="12 mt-3" v-if="groupInfoItem.reminderGroup">
                        <label class="c-green">
                          {{$tc('message.reminderGroup', 1)}}: {{groupInfoItem.reminderGroup.descr}}
                        </label>

                        <b-row class="border-bottom border-c-green mb-2"/>

                        <b-container class="max-height-200-px">
                          <b-row v-for="item in groupInfoItem.reminderGroup.recipients" :key="item">
                            <div><h6>- {{item}}</h6></div>
                          </b-row>
                        </b-container>
                      </b-col>

                      <b-col cols="12 mt-2 c-red" v-if="!groupInfoItem.reminderGroup && !groupInfoItem.notificationGroup">
                        {{$t('message.noGroupConfigured')}}
                      </b-col>
                    </b-row>

                    <b-row class="mt-2">
                      <b-col>
                        <h6>{{$t('info.toChangeConfig')}}:</h6>
                        <h6 :class="`c-green hover-enabled h-c-blue mb-0 ${isMobile ? 'd-block' : 'd-inline-flex'}`"
                            @click="$router.push(first(tabs.services.links))">{{$tc('message.type', 1)}} {{$tc('message.service', 2)}}</h6>
                      </b-col>
                    </b-row>
                  </b-container>
                </b-container>
              </transition>
            </b-col>
          </b-row>
        </b-container>


      <b-container slot="modal-footer" class="w-100">
        <b-row>
          <b-col>
            <b-button class="b-c-green ml-3 float-right mt-2"
                      id="service-modal-close"
                      v-on:click="$refs.editService.hide()">{{$t('message.close')}}</b-button>

            <!-- config[crud], create - read - update - delete -->
            <b-button class="b-c-green float-right mt-2"
                      id="service-modal-save"
                      v-on:click="addService(serviceEdit)"
                      v-if="vehicleId ? config.u : config.c"
                      :disabled="checkEditServiceSaveButton($refs.editServiceForm)">
              {{$t('message.save')}}
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>
    </validation-observer>
    <b-modal ref="serviceDelete" id="serviceDelete" :title="`${$t('message.caution')}!`">
      <b-container>
        <h5>{{$t('message.deleteCheck')}} {{deleteItem.descr}}?</h5>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button id="nw-delete-continue" class="m-1 b-c-green"
                  v-if="config.d" @click="deleteServices(deleteItem)">{{$t('message.saveClose')}}</b-button>

        <b-button id="nw-delete-close" class="m-1 b-c-green" @click="hideDeleteModal()">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>
    <validation-observer ref="editInsuranceForm" v-slot="{ invalidModal }">
    <b-modal ref="editInsuranceModal" id="editInsuranceModal"
             :title="`${$t('message.add')} ${$t('message.item').toLowerCase()}`">
        <b-container>
          <b-row>
            <b-col>
              <b-form-group :label="`${$tc('message.insuranceCompany', 1)} (*)`"
                            label-for="insurance-company">
                <model-list-select id="insurance-company"
                                   option-text="text"
                                   option-value="value"
                                   :placeholder="insuranceCompanyOptions && insuranceCompanyOptions.length
                                                 !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="selectedInsurance.company"
                                   :list="insuranceCompanyOptions">
                </model-list-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <validation-provider :name="`${$t('message.number')} ${$tc('message.insurance', 1).toLowerCase()}`"
                                   :rules="{ min: 0, max: 50 }"
                                   v-slot="{ changed, valid, errors }">
                <b-form-group :label="`${$t('message.number')} ${$tc('message.insurance', 1).toLowerCase()}`"
                              label-for="input-insurance-number">
                  <b-form-input id="input-insurance-number"
                                v-model="selectedInsurance.number"
                                autocomplete="off" type="text"
                                :placeholder="`${$tc('message.example', 1)}: 10235789`"
                                :state="changed && !valid ? false : null">
                  </b-form-input>

                  <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-form-group :label="`${$t('message.date')} ${$t('message.start').toLowerCase()} (*)`"
                            label-for="insurance-start-date">
                <b-input-group>
                  <flat-pickr id="insurance-start-date"
                              :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                              v-model="selectedInsurance.startDate"
                              :config="insuranceStartDateOptions"
                              @on-change="insuranceStartDateOptionChanged">
                  </flat-pickr>
                  <div class="input-group-append">
                    <b-button @click="selectedInsurance.startDate = null"
                              :disabled="!selectedInsurance.startDate">
                      <font-awesome-icon icon="trash"/>
                    </b-button>
                  </div>
                  <div v-if="checkDateValidation(selectedInsurance.startDate).isError" class="my-invalid-feedback">
                    <h6>{{$t('message.date')}} {{$t('message.start').toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                  </div>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-form-group :label="`${$t('message.date')} ${$t('message.expiration').toLowerCase()} (*)`"
                            label-for="input-expiration-date">
                <b-input-group>
                  <flat-pickr id="insurance-end-date"
                              :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                              v-model="selectedInsurance.endDate"
                              :config="insuranceEndDateOptions"
                              @on-change="insuranceEndDateOptionChanged">
                  </flat-pickr>
                  <div class="input-group-append">
                    <b-button @click="selectedInsurance.endDate = null"
                              :disabled="!selectedInsurance.endDate">
                      <font-awesome-icon icon="trash"/>
                    </b-button>
                  </div>
                  <div v-if="checkDateValidation(selectedInsurance.endDate).isError" class="my-invalid-feedback">
                    <h6>{{$t('message.date')}} {{$t('message.start').toLowerCase()}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                  </div>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <validation-provider :name="$t('message.priceYear')"
                                   :rules="{ min: 0, max: 10 }"
                                   v-slot="{ changed, valid, errors }">
                <b-form-group :label="$t('message.priceYear')"
                              label-for="input-insurance-price">
                  <b-input-group append="€">
                    <b-form-input id="input-insurance-price"
                                  v-model="selectedInsurance.price"
                                  autocomplete="off" min="0" step="0.01"
                                  :placeholder="`${$tc('message.example', 1)}: 10235789`"
                                  :state="changed && !valid ? false : null"
                                  @keypress="isNumber($event, selectedInsurance.price)">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <validation-provider :name="$t('message.installment').toLowerCase()"
                                   :rules="{ min: 0, max: 10 }"
                                   v-slot="{ changed, valid, errors }">
                <b-form-group :label="$t('message.installment')"
                              label-for="input-insurance-installment">
                  <b-input-group append="€">
                    <b-form-input id="input-insurance-installment"
                                  v-model="selectedInsurance.installment"
                                  autocomplete="off" min="0" step="0.01"
                                  :placeholder="`${$tc('message.example', 1)}: 10235789`"
                                  :state="changed && !valid ? false : null"
                                  @keypress="isNumber($event, selectedInsurance.installment)">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-form-group :label="`${$t('message.frequency')} ${$t('message.payment').toLowerCase()}`"
                            label-for="input-insurance-paymentFreq">
                <model-list-select id="input-insurance-paymentFreq"
                                   option-text="text"
                                   option-value="value"
                                   :placeholder="paymentOptions && paymentOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="selectedInsurance.paymentFreq"
                                   :list="paymentOptions">
                </model-list-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>


      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button class="b-c-green mr-2" id="insurance-modal-save" v-on:click="addInsurance"
                  v-if="vehicleId ? config.u : config.c"
                  :disabled="checkEditInsuranceSaveButton($refs.editInsuranceForm) || invalidModal">
          {{$t('message.save')}}
        </b-button>

        <b-button class="b-c-green mr-3" id="insurance-modal-close" v-on:click="closeModal()">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>
    </validation-observer>
    <b-modal ref="insuranceDelete" id="insuranceDelete" :title="`${$t('message.caution')}!`">
      <b-container>
        <h5>{{$t('message.deleteCheck')}} {{deleteItem.descr}}?</h5>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button id="nw-insurance-delete-continue" class="m-1 b-c-green"
                  v-if="vehicleId ? config.u : config.c" @click="deleteInsurance(deleteItem)">{{$t('message.saveClose')}}</b-button>

        <b-button id="nw-insurance-delete-close" class="m-1 b-c-green" @click="hideDeleteModal()">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>

    <b-modal ref="addOBUModal" id="addOBUModal" :title="`${$t('message.associate')} ${$t('message.obu')}`">
      <b-container>
        <b-row>
          <b-col>
            <b-form-group :label="$t('message.availableOBU')"
                          label-for="input-tracker">
              <model-list-select id="input-tracker"
                                 option-text="text"
                                 option-value="value"
                                 :placeholder="obuOptions && obuOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                 v-model="associationOBU.imei"
                                 :list="obuOptions">
              </model-list-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button id="nw-obu-add-continue" class="m-1 b-c-green"
                  v-if="vehicleId ? config.u : config.c" :disabled="!associationOBU.imei" @click="addOBU(associationOBU)">{{$t('message.saveClose')}}</b-button>

        <b-button id="nw-obu-add-close" class="m-1 b-c-green" @click="hideAddOBUModal()">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>

    <b-modal ref="obuDelete" id="obuDelete" :title="`${$t('message.caution')}!`">
      <b-container>
        <h5>{{$t('message.deleteCheck')}} {{deleteItem.descr}}?</h5>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <!-- config[crud], create - read - update - delete -->
        <b-button id="nw-obu-delete-continue" class="m-1 b-c-green"
                  v-if="vehicleId ? config.u : config.c" @click="deleteOBU(deleteItem)">{{$t('message.saveClose')}}</b-button>

        <b-button id="nw-obu-delete-close" class="m-1 b-c-green" @click="hideDeleteModal()">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>

    <b-modal ref="saveCheckModal" id="saveCheckModal" :title="`${$t('message.caution')}!`"
             no-close-on-backdrop no-close-on-esc hide-header-close>
      <b-container>
        <h5>{{$t('warnings.notSavedTabChanges')}}</h5>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button id="nw-save-check-continue" class="m-1 b-c-green" @click="hideSaveCheckModal(true)">{{$t('message.continue')}}</b-button>

        <b-button id="nw-save-check-close" class="m-1 b-c-green" @click="hideSaveCheckModal(false)">{{$t('message.close')}}</b-button>
      </div>
    </b-modal>

    <b-modal ref="groupInfo" id="group-info" :title="`${$t('message.info')}`"
             no-close-on-backdrop no-close-on-esc hide-header-close>
      <b-container>
        <b-row>
          <b-col>
            <h6>{{$t('info.willBeEnabledNotification')}}.</h6>
          </b-col>
        </b-row>

        <b-row v-if="vehicleUsageNotification && vehicleUsageNotification.active
        && (vehicleUsageNotification.notificationRecipientId || vehicleUsageNotification.reminderRecipientId)">
          <b-col cols="12 mt-3" v-if="vehicleUsageNotification.notificationGroup">
            <label class="c-green">
              {{$tc('message.notificationGroup', 1)}}:
              {{vehicleUsageNotification.notificationGroup.descr || $tc('message.emptyValue', 1)}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"/>

            <b-container class="max-height-200-px">
              <b-row v-for="item in vehicleUsageNotification.notificationGroup.recipients" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12 mt-3" v-if="vehicleUsageNotification.reminderGroup">
            <label class="c-green">
              {{$tc('message.reminderGroup', 1)}}: {{vehicleUsageNotification.reminderGroup.descr || $tc('message.emptyValue', 1)}}
            </label>

            <b-row class="border-bottom border-c-green mb-2"/>

            <b-container class="max-height-200-px">
              <b-row v-for="item in vehicleUsageNotification.reminderGroup.recipients" :key="item">
                <div><h6>- {{item}}</h6></div>
              </b-row>
            </b-container>
          </b-col>

          <b-col cols="12" class=" mt-2 c-red" v-if="!vehicleUsageNotification.reminderGroup && !vehicleUsageNotification.notificationGroup">
            {{$t('message.noGroupConfigured')}}
          </b-col>
        </b-row>

        <b-row v-else cols="12" class="mt-2 c-red">
          <b-col>
            {{$t('message.noGroupConfigured')}}
          </b-col>
        </b-row>

        <b-row v-if="vehicleUsageNotification.active" class="mt-2">
          <b-col>
            <h6>{{$t('info.toChangeConfig')}}:</h6>
            <h6 :class="`c-green hover-enabled h-c-blue mb-0 ${isMobile ? 'd-block' : 'd-inline-flex'}`"
                @click="$router.push(first(tabs.vehicleUsage.links))">{{$tc('message.recipients', 2)}}</h6>
          </b-col>
        </b-row>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button class="b-c-green" id="group-info-ok-button" @click="$refs.groupInfo.hide()"> Ok
        </b-button>
      </div>
    </b-modal>

    <b-modal ref="hasObuAssociated" id="hasObuAssociated"
             no-close-on-backdrop no-close-on-esc hide-header-close>
      <b-container>
        <b-row>
          <b-col>
            <h6>{{$t('info.canNotDisableIfHasOBU')}}.</h6>
          </b-col>
        </b-row>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button class="b-c-green" id="has-obu-ok-button" @click="$refs.hasObuAssociated.hide()"> Ok
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { first, last, keys, cloneDeep, find, orderBy, filter, isEmpty, findIndex } from 'lodash'
import { DateTime } from 'luxon'
import { Italian } from 'flatpickr/dist/l10n/it.js'
import notificationType from '../../plugins/notification-field-name'
import logKeys from '@/plugins/log-keys';
import { isNil } from 'lodash'

export default {
  name: 'EditVehicle',
  data () {
    const vehicleId = this.$route.params.id !== 'new' ? this.$route.params.id : null
    const genericDateConfig = {
      dateFormat: 'Y-m-d',
      mode: 'single',
      altFormat: 'D j M Y',
      altInput: true,
      locale: Italian,
      minDate: '',
      maxDate: '',
      disable: []
    }
    const genericDateConfigStatic = {
      dateFormat: 'Y-m-d',
      mode: 'single',
      altFormat: 'D j M Y',
      altInput: true,
      locale: Italian,
      minDate: '',
      maxDate: '',
      disable: [],
      static: true
    }
    const basicData = {
      plateNumber: null,
      chassisNumber: null,
      kmCost: null,
      avgConsumption: null,
      description: null,
      type: {
        classification: null,
        equipment: null,
        fuelType: null,
        brand: null,
        model: null,
        payload: null,
        engineDisplacement: null,
        kw: null,
        seats: null,
        emissionStandard: null,
        length: null,
        width: null,
        traction: '0'
      },
      ownership: {
        rental: {
          company: null,
          price: null,
          redemptionPrice: null,
          relief: null,
          kmYear: null,
          maxKm: null
        },
        leasing: {
          redemptionPrice: null
        },
        purchase: {
          price: null,
          km: null
        }
      },
      measures: [],
      extraEquipment: {},
      registry: {
        behalfOf: null,
        codes: []
      },
      registrationDate: null,
      insurance: {},
      services: [],
      ownUse: true
    }
    const config = this.$route.meta.config

    return {
      config,
      first,
      basicData,
      DateTime,
      optionRegistrationDate: cloneDeep(genericDateConfig),
      lastMeasureOption: cloneDeep(genericDateConfig),
      rentalOptionDateStart: cloneDeep(genericDateConfig),
      rentalOptionDateEnd: cloneDeep(genericDateConfig),
      leasingOptionDateStart: cloneDeep(genericDateConfig),
      leasingOptionDateEnd: cloneDeep(genericDateConfig),
      purchaseOptionDateStart: cloneDeep(genericDateConfig),
      alboRegistrationOption: cloneDeep(genericDateConfig),
      disablingDateConfig: cloneDeep(genericDateConfig),
      serviceSelectedOption: cloneDeep(genericDateConfigStatic),
      insuranceStartDateOptions: cloneDeep(genericDateConfigStatic),
      insuranceEndDateOptions: cloneDeep(genericDateConfigStatic),
      // main data input
      vehicleId,
      editedOwnership: { owner: null },
      editedMeasures: {
        km: null
      },
      mainBody: cloneDeep(basicData),
      originalMainBody: {},
      foundedVehicle: {},
      // end main data input
      // select data options
      typeOptions: [],
      fuels: [],
      properties: [],
      insuranceCompanyOptions: [],
      companies: [],
      states: [],
      useCase: [],
      sectors: [],
      ownershipType: [],
      equipmentOptions: [],
      classificationOptions: [],
      alboCodes: [],
      tabs: {
        mainBody: {
          index: 0,
          active: true,
          name: 'essential',
          links: ['/configuration/classification', '/configuration/fuel_type', '/configuration/equipment']
        },
        details: {
          index: 1,
          active: false,
          name: 'extra',
          links: ['/configuration/vehicle_state', '/configuration/owner', '/configuration/company', '/configuration/activity_sector']
        },
        vehicleUsage: {
          index: 2,
          active: false,
          name: 'use',
          links: ['/recipients']
        },
        albo: {
          index: 3,
          active: false,
          name: 'albo',
          links: ['/configuration/registry_code']
        },
        equip: {
          index: 4,
          active: false,
          name: 'equipment',
          links: []
        },
        services: {
          index: 5,
          active: false,
          name: 'services',
          links: ['/configuration/service_type', '/recipients']
        },
        insurance: {
          index: 6,
          active: false,
          name: 'insurance',
          links: ['/configuration/insurance_company']
        },
        docs: {
          index: 7,
          active: false,
          links: []
        },
        obu: {
          index: 8,
          active: false,
          name: 'obu',
          links: []
        }
      },
      currentTab: 0,
      servicesFields: [],
      servicesItems: [],
      serviceEdit: {
        type: '',
        pin: null,
        number: null,
        expDate: null
      },
      serviceEditOldItem: null,
      insuranceItems: [],
      insuranceFields: [],
      selectedInsurance: { company: null },
      // end select data options
      transitionName: 'slide-left',
      alboCodesSelected: [],
      showEquipment: false,
      ownershipTypeSelected: null,
      inOperation: true,
      dateErrors: false,
      itemChanged: false,
      mainBodyWatcher: null,
      servicesItemsWatcher: null,
      editedOwnershipWatcher: null,
      alboCodesSelectedWatcher: null,
      editedMeasuresWatcher: null,
      deleteItem: {},
      disabledOptions: [],
      paymentOptions: [],
      disabledSelected: true,
      tracker: {},
      associationOBU: {},
      freeTrackers: [],
      obuOptions: [],
      oldTabIndex: 0,
      newTabIndex: 0,
      lastMeasure: {},
      allRecipientGroups: [],
      vehicleSchedulingEvents: [],
      serviceInfo: {},
      isNewService: false,
      showServiceInfoGroups: false,
      groupInfoItem: {},
      isMobile: false,
      vehicleUsageNotification: {
        active: false,
        eventId: null,
        notificationRecipientId: '',
        reminderRecipientId: '',
        reminderGroup: {},
        notificationGroup: {}
      },
      ownershipReminderGroupWatcher: null,
      ownershipNotificationGroupWatcher: null,
      saveCheckModalIsActive: false
    }
  },
  watch: {
    editedOwnership (val) {
      // TODO try to use "this.mainBody.ownership.owner" instead "editedOwnership.owner" in "#input-property"'s v-model
      this.mainBody.ownership.owner = val.owner
    },
    'serviceEdit.type' (val) {
      this.checkSelectedService(val)
      this.showServiceInfoGroups = val !== ''
    },
    'vehicleUsageNotification.notificationRecipientId' (val) {
      this.updateNotificationGroups(val, this.vehicleUsageNotification.reminderRecipientId)
    },
    'vehicleUsageNotification.reminderRecipientId' (val) {
      this.updateNotificationGroups(this.vehicleUsageNotification.notificationRecipientId, val)
    },
    'vehicleUsageNotification.active' (val) {
      if (val) {
        this.updateNotificationGroups(this.vehicleUsageNotification.notificationRecipientId, this.vehicleUsageNotification.reminderRecipientId)
      }
    },
    ownershipTypeSelected (val) {
      this.checkWatcher(true)
      const allEvents = this.vehicleSchedulingEvents
      const type = `${notificationType.OWNERSHIP.TYPE}=${val}`
      this.vehicleUsageNotification = {
        srcFieldName: type,
        active: false,
        notificationRecipientId: '',
        reminderRecipientId: ''
      }
      let date = null
      if (val === 'loan' || val === 'leasing' || val === 'rental') {
        let obj = {}
        if (val === 'rental') {
          obj = this.mainBody.ownership.rental
          date = obj.endDate
        } else {
          obj = this.mainBody.ownership.leasing
          date = obj.endDate
        }
      }

      if (date && val !== '') {
        allEvents.forEach(event => {
          if (event.date === date && event.srcFieldName === type) {
            this.vehicleUsageNotification = event
          }
        })
      }
      this.addWatcher()
    },
    currentTab (newVal, oldVal) {
      this.oldTabIndex = oldVal
      this.newTabIndex = newVal
      if (!this.saveCheckModalIsActive) this.checkTab(oldVal, newVal)
      else this.saveCheckModalIsActive = false
    }
  },
  mounted () {
    this.fetchData()
    this.$root.$on('change-lang', () => {
      if (this.$el.isConnected) {
        this.getStaticOptions()
      }
      this.$nextTick(() => {
        if (this && this.$refs && this.$refs.mainForm) this.$refs.mainForm.validate()
      })
    })
    this.$root.$on('resize', () => {
      this.checkWindowWidth()
    })
  },
  methods: {
    clickedLink (link) {
      if (link.href === '/organization/operation') {
        this.pushBack({ name: 'Operation', query: { vehicle: this.vehicleId } })
      } else if (link.href) {
        this.pushBack(link.href)
      }
    },
    checkWindowWidth () {
      const size = Vue.myGetWindowSize()
      this.isMobile = (size !== 'md' && size !== 'lg' && size !== 'xl')
    },
    checkWatcher (reset) {
      this.$root.$emit('activeLoader', true)
      if (this.mainBodyWatcher) this.mainBodyWatcher()
      if (this.servicesItemsWatcher) this.servicesItemsWatcher()
      if (this.editedOwnershipWatcher) this.editedOwnershipWatcher()
      if (this.alboCodesSelectedWatcher) this.alboCodesSelectedWatcher()
      if (this.editedMeasuresWatcher) this.editedMeasuresWatcher()
      if (this.ownershipReminderGroupWatcher) this.ownershipReminderGroupWatcher()
      if (this.ownershipNotificationGroupWatcher) this.ownershipNotificationGroupWatcher()
      if (reset) this.itemChanged = false
    },
    addWatcher () {
      this.mainBodyWatcher = this.$watch('mainBody', () => {
        this.itemChanged = true
      }, { deep: true })
      this.editedOwnershipWatcher = this.$watch('editedOwnership', () => {
        this.itemChanged = true
      }, { deep: true })
      this.alboCodesSelectedWatcher = this.$watch('alboCodesSelected', () => {
        this.itemChanged = true
      }, { deep: true })
      this.servicesItemsWatcher = this.$watch('servicesItems', () => {
        this.itemChanged = true
      }, { deep: true })
      this.editedMeasuresWatcher = this.$watch('editedMeasures', () => {
        this.itemChanged = true
      }, { deep: true })
      this.ownershipReminderGroupWatcher = this.$watch('vehicleUsageNotification.notificationRecipientId', () => {
        this.itemChanged = true
      })
      this.ownershipNotificationGroupWatcher = this.$watch('vehicleUsageNotification.reminderRecipientId', () => {
        this.itemChanged = true
      })
      this.$root.$emit('activeLoader', false)
    },
    fetchData () {
      this.checkWatcher(true)
      this.getOptions().then(() => this.getVehicle().then(() => {
        const lastTabIndex = Vue.getLocalStorage('lastTabIndex')
        const vehicleId = this.vehicleId
        const query = this.$route.query
        if (!isNil(lastTabIndex) && lastTabIndex && lastTabIndex.vehicleId && lastTabIndex.vehicleId === vehicleId) {
          this.changeTab(lastTabIndex.tabIndex, true)
        } else {
          if (query.tab) {
            let foundedTab = {}
            const tabs = keys(this.tabs)
            tabs.forEach(tab => {
              if (this.tabs[tab].name === query.tab) foundedTab = this.tabs[tab]
            })
            if (foundedTab && foundedTab.index) this.changeTab(foundedTab.index, true)
          } else {
            this.changeTab(0, true)
          }
          Vue.deleteLocalStorage('lastTabIndex')
        }
      }))
    },
    getVehicle () {
      if (this.vehicleId) {
        return Vue.myGet(this, `/vehicles/${this.vehicleId}`, 'vehicles').then((vehicles) => {
          const editedVehicle = first(vehicles)
          const measures = editedVehicle.measures && editedVehicle.measures.length !== 0
            ? last(editedVehicle.measures) : [{ km: 0, date: null }]

          if (editedVehicle.ownership) {
            if (!editedVehicle.ownership.rental) editedVehicle.ownership.rental = { company: null }
            if (!editedVehicle.ownership.leasing) editedVehicle.ownership.leasing = { startDate: null, endDate: null }
            if (!editedVehicle.ownership.purchase) editedVehicle.ownership.purchase = { date: null }
            this.editedOwnership.owner = editedVehicle.ownership.owner || null
            this.ownershipTypeSelected = this.getOwnershipType(cloneDeep(editedVehicle.ownership))
          } else {
            editedVehicle.ownership = {
              rental: { company: null },
              leasing: { startDate: null, endDate: null },
              purchase: { date: null }
            }
            this.editedOwnership.owner = null
            this.ownershipTypeSelected = null
          }
          if (!editedVehicle.type) editedVehicle.type = { classification: null, equipment: null, fuelType: null }
          if (!editedVehicle.extraEquipment) editedVehicle.extraEquipment = {}
          if (!editedVehicle.registry) editedVehicle.registry = { codes: [] }
          if (editedVehicle.ownUse !== true && editedVehicle.ownUse !== false) editedVehicle.ownUse = true

          this.mainBody = cloneDeep(editedVehicle)
          this.originalMainBody = cloneDeep(editedVehicle)

          this.editedMeasures = !editedVehicle.measures || editedVehicle.measures.length === 0
            ? {
              km: null
            }
            : last(editedVehicle.measures)
          this.lastMeasure = cloneDeep(measures)
          if (editedVehicle.services && editedVehicle.services.length !== 0) {
            this.getEditedServices(cloneDeep(editedVehicle.services))
          }
          if (editedVehicle.insurances && editedVehicle.insurances.length !== 0) {
            this.getEditedInsurances(cloneDeep(editedVehicle.insurances))
          }
          this.inOperation = !this.mainBody.disablingDate
          this.showEquipment = !isEmpty(editedVehicle.type.equipment)
          this.optionRegistrationDate.maxDate = DateTime.local().toSQLDate()
          this.populateAlboCodes(cloneDeep(editedVehicle.registry.codes))
          if (editedVehicle.imei) {
            Vue.myGet(this, `/trackers?imei=${editedVehicle.imei}`, 'trackers').then((trackers) => {
              this.tracker = first(trackers)
              this.addWatcher()
            }).catch(error => {
              Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
              this.addWatcher()
            })
          } else {
            Vue.myGet(this, '/trackers?assigned=false', 'trackers').then((trackers) => {
              this.freeTrackers = trackers
              this.addWatcher()
            }).catch(error => {
              Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
              this.addWatcher()
            })
          }
          return this.originalMainBody
        }).catch(error => {
          Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
          this.addWatcher()
          return false
        })
      } else {
        this.originalMainBody = cloneDeep(this.basicData)
        this.mainBody = cloneDeep(this.basicData)
        this.tracker = {}
        this.ownershipTypeSelected = null
        this.showEquipment = false
        this.inOperation = true
        this.optionRegistrationDate.maxDate = DateTime.local().toSQLDate()
        this.addWatcher()
        return new Promise(resolve => resolve())
      }
    },
    getOwnershipType (ownership) {
      const rentalType = ownership.rental && ownership.rental.type
      const rentalEnd = ownership.rental && ownership.rental.endDate && DateTime.fromSQL(ownership.rental.endDate)
      const leasingEnd = ownership.leasing && ownership.leasing.endDate && DateTime.fromSQL(ownership.leasing.endDate)
      const purchaseStart = ownership.purchase && ownership.purchase.date && DateTime.fromSQL(ownership.purchase.date)
      if (purchaseStart) return 'property'
      if (rentalEnd > leasingEnd) return rentalType === 'rental' ? 'rental' : 'loan'
      if (leasingEnd > rentalEnd) return 'leasing'
      if (leasingEnd) return 'leasing'
      if (rentalEnd) return rentalType === 'rental' ? 'rental' : 'loan'
      return null
    },
    populateAlboCodes (items) {
      this.alboCodesSelected = []
      if (items) {
        items.forEach((item) => {
          const foundedItem = find(this.alboCodes, obj => obj.value === item)
          if (foundedItem) this.alboCodesSelected.push({ value: item, text: foundedItem.text })
        })
      }
    },
    pushBack (obj) {
      const backObj = obj || { name: 'Vehicle' }
      if (this.itemChanged) this.$root.$emit('go-back-modal', backObj)
      else this.$router.push(backObj)
    },
    checkEquipment (val) {
      if (!val) this.mainBody.type.equipment = ''
    },
    checkTab (oldTabIndex, newTabIndex) {
      const oldTab = find(this.tabs, obj => obj.index === oldTabIndex)
      const newTab = find(this.tabs, obj => obj.index === newTabIndex)

      if (this.itemChanged) {
        if (oldTab) oldTab.active = true
        if (newTab) newTab.active = false
        this.saveCheck()
      } else {
        if (oldTab) oldTab.active = false
        if (newTab) newTab.active = true
        this.transitionName = oldTabIndex > newTabIndex ? 'slide-right' : 'slide-left'
        this.changeTab(newTab.index, true)
      }
      Vue.setLocalStorage('lastTabIndex', { vehicleId: this.vehicleId, tabIndex: this.itemChanged ? oldTabIndex : newTabIndex })
    },
    saveCheck () {
      this.$refs.saveCheckModal.show()
    },
    hideSaveCheckModal (isNew) {
      this.$refs.saveCheckModal.hide()
      const index = isNew ? this.newTabIndex : this.oldTabIndex
      Vue.deleteLocalStorage('lastIndex')
      this.checkWatcher(isNew)
      Vue.setLocalStorage('lastTabIndex', { vehicleId: this.vehicleId, tabIndex: index })
      if (isNew || isNew === 0) this.fetchData()
      else {
        this.saveCheckModalIsActive = true
        this.changeTab(index)
      }
    },
    changeTab (newIndex, triggerReset) {
      this.checkWatcher(triggerReset)
      const newTab = find(this.tabs, obj => obj.index === newIndex)
      const links = []
      if (newTab && newTab.links) {
        if (this.vehicleId) {
          links.push({ href: '/organization/operation' })
        }
        newTab.links.forEach(link => {
          links.push({ href: link })
        })
      }
      if (newTab) newTab.active = true
      Vue.deleteLocalStorage('lastIndex')
      Vue.setLocalStorage('lastTabIndex', { vehicleId: this.vehicleId, tabIndex: newIndex })
      this.$root.$emit('change-links', links)
      if (newIndex || newIndex === 0) this.currentTab = newIndex
      this.$refs.saveCheckModal.hide()
      this.addWatcher()
    },
    checkDisablingDate (val) {
      if (val) {
        this.mainBody.disablingDate = null
        if (this.mainBody.imei) this.$refs.hasObuAssociated.show()
      }
    },
    getOptions () {
      const axiosArray = []
      this.getStaticOptions()
      axiosArray.push(this.getRecipients())
      axiosArray.push(this.getScheduling(null))
      axiosArray.push(this.getEnum('fuel_type', 'fuels', true))
      axiosArray.push(this.getEnum('owner', 'properties', true))
      axiosArray.push(this.getEnum('insurance_company', 'insuranceCompanyOptions', true))
      axiosArray.push(this.getEnum('company', 'companies', true))
      axiosArray.push(this.getEnum('vehicle_state', 'states', true))
      axiosArray.push(this.getEnum('activity_sector', 'sectors', true))
      axiosArray.push(this.getEnum('registry_code', 'alboCodes'), false)
      axiosArray.push(this.getEnum('service_type', 'typeOptions', true, true))
      axiosArray.push(this.getEnum('equipment', 'equipmentOptions', true))
      axiosArray.push(this.getEnum('classification', 'classificationOptions', true))
      return Vue.myAll(axiosArray).then(r => r)
    },
    getStaticOptions () {
      this.getDisabledOptions()
      this.getPaymentOptions()
      this.getInsuranceFields()
      this.getServiceFields()
      this.getOwnershipTypeOptions()
      this.getInsuranceFields()
      this.getUseCaseOptions()
    },
    getDisabledOptions () {
      this.disabledOptions = [
        { value: true, text: this.$t('message.yes') },
        { value: false, text: this.$t('message.no') }
      ]
    },
    getPaymentOptions () {
      this.paymentOptions = [
        { value: 1, text: this.$t('message.monthly') },
        { value: 3, text: this.$t('message.quarterly') },
        { value: 6, text: this.$t('message.semiannual') },
        { value: 12, text: this.$t('message.yearly') }
      ]
    },
    getInsuranceFields () {
      this.insuranceFields = [
        // config[crud], create - read - update - delete
        { key: 'edit', label: '', hideInTable: !this.config.u && !this.config.r },
        { key: 'delete', label: '', hideInTable: !this.config.d },
        { key: 'number', label: `${this.$tc('message.number', 1)} ${this.$tc('message.insurance', 1)}`, sortable: true, sortDirection: 'desc' },
        { key: 'descr', label: `${this.$t('message.insuranceCompany')}`, sortable: true, sortDirection: 'desc' },
        { key: 'parsedEndDate', label: `${this.$t('message.date')} ${this.$t('message.expiration').toLowerCase()}`, sortable: true, sortDirection: 'desc', isDate: true },
        { key: 'parsedPaymentFrequency', label: `${this.$t('message.frequency')} ${this.$t('message.payment').toLowerCase()}`, sortable: true, sortDirection: 'desc' }
      ]
    },
    getUseCaseOptions () {
      this.useCase = [
        { value: true, text: this.$t('message.own') },
        { value: false, text: this.$t('message.thirdParties') }
      ]
    },
    getServiceFields () {
      this.servicesFields = [
        // config[crud], create - read - update - delete
        { key: 'edit', label: '', hideInTable: !this.config.u && !this.config.r },
        { key: 'info', label: '', hideInTable: !this.config.u },
        { key: 'delete', label: '', hideInTable: !this.config.d },
        { key: 'descr', label: `${this.$t('message.description')}`, sortable: true, sortDirection: 'desc' },
        { key: 'parsedExpDate', label: `${this.$t('message.date')} ${this.$t('message.expiration').toLowerCase()}`, sortable: true, sortDirection: 'desc', isDate: true },
        { key: 'number', label: `${this.$t('message.number')}`, sortable: true, sortDirection: 'desc' },
        { key: 'pin', label: 'PIN', sortable: true, sortDirection: 'desc', hideInTable: true },
        { key: 'eventActive', label: `${this.$tc('message.enabled', 2)}`, sortable: true, sortDirection: 'desc', tdClass: 'ellipse' },
        { key: 'notificationListName', label: `${this.$tc('message.notificationGroup', 1)}`, hideInTable: true },
        { key: 'reminderListName', label: `${this.$tc('message.reminderGroup', 1)}`, hideInTable: true },
        { key: 'eventNotified', label: `${this.$tc('message.notified', 1)}`, sortable: true, sortDirection: 'desc', tdClass: 'ellipse' },
        { key: 'eventExpired', label: `${this.$tc('message.expired', 1)}`, sortable: true, sortDirection: 'desc', tdClass: 'ellipse' },
        { key: 'note', label: `${this.$tc('message.note', 2)}`, sortable: true, sortDirection: 'desc', tdClass: 'ellipse', hideInTable: true }
      ]
    },
    getOwnershipTypeOptions () {
      this.ownershipType = [
        { value: 'rental', text: this.$tc('message.rental', 1) },
        { value: 'leasing', text: 'Leasing' },
        { value: 'property', text: this.$tc('message.property', 1) },
        { value: 'loan', text: this.$tc('message.loan', 1) }
      ]
    },
    alboSelection (items) {
      this.alboCodesSelected = items
    },
    getRecipients () {
      return Vue.myGet(this, '/recipients_list', 'recipients').then((recipients) => {
        const allGroups = []
        allGroups.push({ id: '', descr: recipients.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') })
        recipients.forEach(recipient => {
          allGroups.push(recipient)
        })
        this.allRecipientGroups = allGroups
        this.$root.$emit('activeLoader', false)
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        this.addWatcher()
        return false
      })
    },
    updateNotificationGroups (notificationId, reminderId) {
      const allGroups = this.allRecipientGroups

      if (notificationId !== '') {
        const foundedItem = find(allGroups, obj => obj.id === notificationId)
        if (foundedItem) this.vehicleUsageNotification.notificationGroup = foundedItem
      } else this.vehicleUsageNotification.notificationGroup = {}

      if (reminderId !== '') {
        const foundedItem = find(allGroups, obj => obj.id === reminderId)
        if (foundedItem) this.vehicleUsageNotification.reminderGroup = foundedItem
      } else this.vehicleUsageNotification.reminderGroup = {}

      if (this.vehicleUsageNotification.active && (this.currentTab === 2 || this.currentTab === 5)) { // vehicle usage tab or services
        this.$refs.groupInfo.show()
      }
    },
    getScheduling (item) {
      this.vehicleSchedulingEvents = []
      if (!this.vehicleId) return true
      const srcEntity = 'srcEntity=vehicle'
      const serviceFilter = item && item.type ? `&srcFieldName=${notificationType.SERVICES.TYPE}=${item.type}` : ''
      const vehicleSrc = `&srcObjectId=${this.vehicleId}`
      const dateFilter = item && item.expDate ? '&start_date=' + item.expDate : ''
      const url = `/scheduling?${srcEntity}${serviceFilter}${vehicleSrc}${dateFilter}`

      return Vue.myGet(this, url, 'events').then((events) => {
        const editedItems = []
        events.forEach(event => {
          if (event.srcEntity === 'vehicle' && event.srcObjectId === this.vehicleId) editedItems.push(event)
        })
        this.vehicleSchedulingEvents = editedItems
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        this.addWatcher()
        return false
      })
    },
    getEnum (path, obj, selectionMessage, hasRecipients) {
      const options = []

      return Vue.myGet(this, `/enums/${path}`, 'data').then((items) => {
        if (selectionMessage) {
          options.push({ value: '', text: items.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') })
        }
        items.forEach((item) => {
          if (hasRecipients) {
            options.push({
              value: item.id,
              text: item.descr,
              notificationRecipientId: item.notificationRecipientId,
              reminderRecipientId: item.reminderRecipientId
            })
          } else {
            options.push({ value: item.id, text: item.descr })
          }
        })
        if (obj) {
          this[obj] = options
        }
        return options
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        this.addWatcher()
        return false
      })
    },
    getEditedServices (originalItems) {
      const typeOptions = cloneDeep(this.typeOptions)
      const schedulingEvents = cloneDeep(this.vehicleSchedulingEvents)
      const services = []
      originalItems.forEach((service) => {
        const foundedEnum = find(typeOptions, obj => obj.value === service.type)
        const editedService = service
        editedService.descr = foundedEnum && foundedEnum.text ? foundedEnum.text : ''
        editedService.eventActive = this.$tc('message.emptyValue', 1)
        editedService.eventNotified = this.$tc('message.emptyValue', 1)
        editedService.eventExpired = this.$tc('message.emptyValue', 1)
        editedService.eventId = null
        editedService.eventActiveVal = false
        if (service.expDate) editedService.parsedExpDate = DateTime.fromSQL(service.expDate).toFormat('dd-MM-y')
        if (foundedEnum) {
          const serviceEvent = find(schedulingEvents, obj => obj.date === service.expDate && obj.srcFieldName === `${notificationType.SERVICES.TYPE}=${service.type}`)
          if (serviceEvent) {
            editedService.eventActive = serviceEvent.active ? this.$t('message.yes') : this.$t('message.no')
            editedService.eventNotified = serviceEvent.notified ? this.$t('message.yes') : this.$t('message.no')
            editedService.eventExpired = serviceEvent.expired ? this.$t('message.yes') : this.$t('message.no')
            editedService.eventId = serviceEvent.id
            editedService.eventActiveVal = serviceEvent.active
            if (serviceEvent.notificationRecipientId) {
              const foundedGroup = find(this.allRecipientGroups, obj => obj.id === serviceEvent.notificationRecipientId)
              editedService.notificationList = foundedGroup ? foundedGroup.recipients : []
              editedService.notificationListName = foundedGroup ? foundedGroup.descr : this.$tc('message.emptyValue', 1)
            }
            if (serviceEvent.reminderRecipientId) {
              const foundedGroup = find(this.allRecipientGroups, obj => obj.id === serviceEvent.reminderRecipientId)
              editedService.reminderList = foundedGroup ? foundedGroup.recipients : []
              editedService.reminderListName = foundedGroup ? foundedGroup.descr : this.$tc('message.emptyValue', 1)
            }
          }
        }
        services.push(editedService)
      })
      this.servicesItems = orderBy(services, 'descr')
    },
    editServices (item) {
      this.isNewService = !item
      this.serviceEdit = cloneDeep(item) || {
        type: '',
        pin: null,
        number: null,
        expDate: null
      }
      this.serviceEditOldItem = cloneDeep(item) || null
      this.$refs.editService.show()
    },
    checkEditServiceSaveButton (modal) {
      const hasType = this.serviceEdit.type
      const hasDataErrors = this.dateErrors
      const expDate = this.serviceEdit.expDate
      const disableSchedulingButton = this.checkSelectedService(hasType)

      if (modal && modal.flags && modal.flags && modal.flags.invalid) return true
      return !hasType || hasDataErrors || !expDate || disableSchedulingButton
    },
    checkEditInsuranceSaveButton (modal) {
      const company = this.selectedInsurance.company
      const startDate = this.selectedInsurance.startDate
      const endDate = this.selectedInsurance.endDate

      if (modal && modal.flags && modal.flags && modal.flags.invalid) return true
      return !company || !startDate || !endDate
    },
    checkSelectedService (serviceId) {
      this.groupInfoItem.notificationGroup = null
      this.groupInfoItem.reminderGroup = null
      if (!serviceId || serviceId === '') return true
      let foundedNotificationGroup = false
      let foundedReminderGroup = false
      if (serviceId) {
        const foundedService = find(this.typeOptions, obj => obj.value === serviceId)
        if (foundedService) {
          const notificationGroup = foundedService.notificationRecipientId
          const reminderGroup = foundedService.reminderRecipientId
          if (notificationGroup) {
            const foundedRecipientGroup = find(this.allRecipientGroups, obj => obj.id === notificationGroup)
            foundedNotificationGroup = foundedRecipientGroup && foundedRecipientGroup.recipients &&
              foundedRecipientGroup.recipients.length !== 0
            this.groupInfoItem.notificationGroup = foundedRecipientGroup
          }
          if (reminderGroup) {
            const foundedRecipientGroup = find(this.allRecipientGroups, obj => obj.id === reminderGroup)
            foundedReminderGroup = foundedRecipientGroup && foundedRecipientGroup.recipients &&
              foundedRecipientGroup.recipients.length !== 0
            this.groupInfoItem.reminderGroup = foundedRecipientGroup
          }
        }
      }
      return !foundedNotificationGroup && !foundedReminderGroup && this.serviceEdit.eventActiveVal
    },
    addService (item, exit) {
      const editedServiceObj = cloneDeep(item)
      const originalItem = cloneDeep(this.serviceEditOldItem)
      const allServices = cloneDeep(this.servicesItems)
      let foundedItem = null
      const editedServices = []

      if (item.eventId) {
        this.deleteSchedulingEvent(editedServiceObj.eventId)
        editedServiceObj.eventId = null
      }

      editedServiceObj.descr = find(this.typeOptions, obj => obj.value === item.type).text
      allServices.forEach(service => {
        if (service.type !== item.type || service.expDate !== item.expDate) {
          if (!originalItem || service.type !== originalItem.type || service.expDate !== originalItem.expDate) {
            editedServices.push(service)
          }
        } else foundedItem = service
      })

      if (!foundedItem) {
        editedServices.push(editedServiceObj)
        this.servicesItems = editedServices
        this.addServiceContinue(editedServiceObj, exit)
      } else {
        if (item.eventId ? foundedItem.eventId && foundedItem.eventId !== item.eventId : foundedItem.eventId) {
          this.deleteSchedulingEvent(foundedItem.eventId)
        }
        editedServiceObj.eventActiveVal = item.eventActiveVal !== undefined && item.eventActiveVal !== null ? item.eventActiveVal : false
        editedServices.push(editedServiceObj)
        this.servicesItems = editedServices

        this.addServiceContinue(editedServiceObj, exit)
      }
    },
    addServiceContinue (service, exit) {
      this.itemChanged = true
      const event = service.eventId ? find(this.vehicleSchedulingEvents, obj => obj.id === schedulingObj.eventId) : null

      let myMessage = ''
      const foundedService = find(this.typeOptions, obj => obj.value === service.type)
      const serviceName = foundedService ? foundedService.text : this.$tc('warnings.serviceNotFound', 1).toLowerCase()
      const notificationGroup = foundedService ? foundedService.notificationRecipientId : null
      const reminderGroup = foundedService ? foundedService.reminderRecipientId : null
      const brand = this.mainBody.type && this.mainBody.type.brand ? this.mainBody.type.brand : null
      const model = this.mainBody.type && this.mainBody.type.model ? this.mainBody.type.model : null
      const vehicleName = `${this.mainBody.plateNumber}${brand ? ', ' + brand : ''}${model ? ', ' + model : ''}`

      myMessage += `${this.$tc('message.service', 1)} "${serviceName}"`
      myMessage += ` ${this.$tc('message.ofVehicle', 1).toLowerCase()} "${vehicleName}",`
      myMessage += ` ${this.$tc('message.date', 1).toLowerCase()} "${service.expDate}".`

      const schedulingObj = {
        srcEntity: 'vehicle',
        srcFieldName: `${notificationType.SERVICES.TYPE}=${service.type}`,
        srcObjectId: this.vehicleId,
        reminderRecipientId: notificationGroup,
        notificationRecipientId: reminderGroup,
        date: service.expDate,
        km: null,
        message: myMessage,
        active: service.eventActiveVal
      }

      this.updateScheduling(schedulingObj, event).then(() => {
        this.$refs.editService.hide()
        this.serviceEdit = {
          type: '',
          pin: null,
          number: null,
          expDate: null
        }
        this.saveVehicle(exit)
      })
    },
    addOwnershipTypeNotification (exit) {
      const obj = this.vehicleUsageNotification
      const ownershipType = this.ownershipTypeSelected
      let date = null
      let myMessage = ''
      const brand = this.mainBody.type && this.mainBody.type.brand ? this.mainBody.type.brand : null
      const model = this.mainBody.type && this.mainBody.type.model ? this.mainBody.type.model : null
      const vehicleName = `${this.mainBody.plateNumber}${brand ? ', ' + brand : ''}${model ? ', ' + model : ''}`
      const ownershipTypeMessage = `message.${ownershipType}`
      const actualOwnershipType = this.getOwnershipType(this.mainBody.ownership)

      if (actualOwnershipType !== 'property') {
        if (ownershipType === 'rental') {
          date = this.mainBody.ownership.rental.endDate
        } else {
          date = this.mainBody.ownership.leasing.endDate
        }
        myMessage += `${this.$tc(ownershipTypeMessage, 1)}`
        myMessage += ` ${this.$tc('message.ofVehicle', 1).toLowerCase()} "${vehicleName}",`
        myMessage += ` ${this.$tc('message.date', 1).toLowerCase()} "${date}".`

        const schedulingObj = {
          srcEntity: 'vehicle',
          srcFieldName: obj.srcFieldName,
          srcObjectId: this.vehicleId,
          reminderRecipientId: obj.reminderRecipientId || null,
          notificationRecipientId: obj.notificationRecipientId || null,
          date: date,
          message: myMessage,
          active: obj.active
        }

        this.updateScheduling(schedulingObj, obj).then(() => {
          this.saveVehicle(exit)
        })
      } else {
        this.saveVehicle(exit)
      }
    },
    updateScheduling (schedulingObj, event, hideSuccess) {
      return Vue.putOrPost(this, event && !!event.id, `/scheduling${event && event.id ? '/' + event.id : ''}`, schedulingObj).then(() => {
        if (this && this.$tc) {
          const newStatus = schedulingObj.active ? this.$tc('message.enabled', 3).toLowerCase() : this.$tc('message.disabled', 3).toLowerCase()
          if (!hideSuccess) Vue.success(this, `${this.$tc('message.notification', 1)} ${newStatus}`)
        }
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        throw error
      })
    },
    showServiceInfo (item) {
      this.serviceInfo = item
      this.$refs.serviceInfo.show()
    },
    checkDeleteService (item) {
      this.deleteItem = item
      this.$refs.serviceDelete.show()
    },
    deleteServices (item) {
      this.servicesItems = filter(this.servicesItems, obj => obj !== item)
      const event = item.eventId
      if (event) {
        this.deleteSchedulingEvent(event).then(() => {
          this.saveVehicle()
        })
      } else {
        this.saveVehicle()
      }
    },
    deleteSchedulingEvent (id) {
      return Vue.myDelete(this, `/scheduling/${id}`).then(() => {
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    editInsurance (item) {
      this.selectedInsurance = item || { company: null, startDate: null, endDate: null }
      // this.selectedInsurance = item ? cloneDeep(item) : { company: null, startDate: null, endDate: null }
      this.$refs.editInsuranceModal.show()
    },
    getEditedInsurances (originalItems) {
      const insurances = []
      originalItems.forEach((insurance) => {
        const foundedPaymentFreq = find(this.paymentOptions, obj => parseInt(obj.value) === parseInt(insurance.paymentFreq))
        const foundedEnum = find(this.insuranceCompanyOptions, obj => obj.value === insurance.company)
        const editedInsurance = insurance
        editedInsurance.descr = foundedEnum && foundedEnum.text ? foundedEnum.text : ''
        editedInsurance.parsedPaymentFrequency = foundedPaymentFreq ? foundedPaymentFreq.text : this.$tc('message.emptyValue', 1)
        if (insurance.endDate) editedInsurance.parsedEndDate = DateTime.fromSQL(insurance.endDate).toFormat('dd-MM-y')
        insurances.push(editedInsurance)
      })
      this.insuranceItems = orderBy(insurances, 'number')
    },
    checkDeleteInsurance (item) {
      this.deleteItem = item
      this.$refs.insuranceDelete.show()
    },
    deleteInsurance (item) {
      const foundedItem = find(this.insuranceItems, obj => obj === item)
      if (foundedItem) {
        this.insuranceItems = filter(this.insuranceItems, obj => obj !== item)
      }
      this.saveVehicle()
    },
    addInsurance () {
      const editedInsuranceObj = this.selectedInsurance
      editedInsuranceObj.descr = find(this.insuranceCompanyOptions, obj => obj.value === this.selectedInsurance.company).text
      const foundedItem = find(this.insuranceItems, obj => obj === editedInsuranceObj)
      if (!foundedItem) {
        this.insuranceItems.push(editedInsuranceObj)
      } else {
        const index = findIndex(this.insuranceItems, obj => obj === editedInsuranceObj)
        this.insuranceItems[index] = editedInsuranceObj
      }
      this.itemChanged = true
      this.$refs.editInsuranceModal.hide()
      this.saveVehicle(false)
    },
    hideDeleteModal () {
      this.$refs.serviceDelete.hide()
      this.$refs.insuranceDelete.hide()
      this.$refs.obuDelete.hide()
      this.deleteItem = {}
    },
    openAddOBUModal () {
      const editedTrackers = []
      this.$refs.addOBUModal.show()
      this.associationOBU = {}
      this.freeTrackers.forEach((tracker) => {
        editedTrackers.push({
          text: tracker.serialNumber,
          value: tracker.imei
        })
      })
      this.obuOptions = editedTrackers
    },
    hideAddOBUModal () {
      this.$refs.addOBUModal.hide()
      this.associationOBU = {}
    },
    addOBU (item) {
      this.mainBody.imei = item.imei
      this.saveVehicle()
    },
    openDeleteOBUModal () {
      this.deleteItem = this.tracker
      this.deleteItem.descr = this.tracker.serialNumber
      this.$refs.obuDelete.show()
    },
    deleteOBU () {
      this.mainBody.imei = null
      this.saveVehicle()
    },
    measuresHasErrors (isDate) {
      const newKm = this.editedMeasures.km
      const newDate = this.editedMeasures.date
      if (!this.itemChanged) return null

      if (!newKm && !newDate) return null
      if (isDate) {
        if (newKm) {
          return !newDate
        }
      } else {
        if (newDate) {
          return !newKm && newKm !== 0
        }
      }
    },
    checkMainSaveButton () {
      const dateErrors = this.dateErrors
      const mainErrors = false
      const itemChanged = this.itemChanged
      const plateNumber = this.mainBody.plateNumber
      const measuresErrors = this.measuresHasErrors() || this.measuresHasErrors(true)
      const currentTab = this.currentTab
      const ownerType = this.getOwnershipType(this.mainBody.ownership)
      const currentOwnershipType = this.ownershipTypeSelected
      const purchaseObj = this.mainBody.ownership.purchase
      let disableByProperty = false
      const allRecipientGroups = this.allRecipientGroups
      let hasMailList = true
      const vehicleUsageNotification = this.vehicleUsageNotification

      if (currentTab === 2 && ownerType === 'property' && currentOwnershipType !== 'property') {
        disableByProperty = !(!purchaseObj.date && !purchaseObj.km && !purchaseObj.price)
      }

      if (currentOwnershipType !== 'property' && currentTab === this.tabs.vehicleUsage.index) {
        if (currentOwnershipType === 'rental' || currentOwnershipType === 'loan') {
          disableByProperty = !this.mainBody.ownership.rental.endDate
        }
        if (currentOwnershipType === 'leasing') {
          disableByProperty = !this.mainBody.ownership.leasing.endDate
        }
      }

      if (!disableByProperty && vehicleUsageNotification.active) {
        if (vehicleUsageNotification.notificationRecipientId || vehicleUsageNotification.reminderRecipientId) {
          let hasNotification = vehicleUsageNotification.active
          let hasReminder = vehicleUsageNotification.active
          if (vehicleUsageNotification.notificationRecipientId) {
            const foundedRecipientGroup = find(allRecipientGroups, obj => obj.id === vehicleUsageNotification.notificationRecipientId)
            hasNotification = foundedRecipientGroup ? foundedRecipientGroup.recipients.length !== 0 : false
          }
          if (vehicleUsageNotification.reminderRecipientId) {
            const foundedRecipientGroup = find(allRecipientGroups, obj => obj.id === vehicleUsageNotification.reminderRecipientId)
            hasReminder = foundedRecipientGroup ? foundedRecipientGroup.recipients.length !== 0 : false
          }
          hasMailList = hasNotification && hasReminder
        }
      }

      return dateErrors || mainErrors || !itemChanged || !plateNumber || !!measuresErrors || disableByProperty || !hasMailList
    },
    saveVehicle (close) {
      const vehicle = this.assembleVehicle()
      const currentTab = this.getCurrentTab()

      if (vehicle.disablingDate) {
        this.disableAllNotifications(vehicle.id).then(() => {
          this.updateVehicle(vehicle, currentTab, close)
        })
      } else {
        this.updateVehicle(vehicle, currentTab, close)
      }
    },
    updateVehicle (vehicle, currentTab, close) {
      Vue.putOrPost(this, !!vehicle && vehicle.id, `/vehicles${vehicle.id ? '/' + vehicle.id : ''}`, vehicle, false).then((r) => {
        this.logData(vehicle.id || r.data.id, !!this.vehicleId)

        if (close) {
          this.itemChanged = false
          this.pushBack()
        } else {
          this.$router.push({ name: 'RefreshPage', params: { id: vehicle.id || r.data.id, name: 'EditVehicle' }, query: { tab: currentTab.name } })
        }
        Vue.success(this, `${this.$tc('message.vehicle', 1)} ${this.$t('success.putSuccess')}`)
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
      })
    },
    closeModal () {
      const vehicle = this.assembleVehicle()
      const currentTab = this.getCurrentTab()
      this.$refs.editInsuranceModal.hide()
      this.$router.push({ name: 'RefreshPage', params: { id: vehicle.id || r.data.id, name: 'EditVehicle' }, query: { tab: currentTab.name } })
    },
    disableAllNotifications (vehicleId) {
      return Vue.myGet(this, `/scheduling?srcObjectId=${vehicleId}`, 'events').then(events => {
        const axiosArray = []

        events.forEach(event => {
          if (event.srcObjectId === vehicleId) axiosArray.push(this.disableNotification(event))
        })
        return Vue.myAll(axiosArray).then(() => {
          return true
        })
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return true
      })
    },
    disableNotification (item) {
      if (item.active) {
        const editedItem = item

        editedItem.active = false

        return Vue.putOrPost(this, true, `/scheduling/${item.id}`, editedItem).then(() => {
          return true
        }).catch(error => {
          Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
          return true
        })
      }

      return new Promise(resolve => resolve(true))
    },
    assembleVehicle () {
      const vehicle = cloneDeep(this.mainBody)

      vehicle.measures = this.getMeasures(vehicle.measures, cloneDeep(this.editedMeasures))
      vehicle.services = this.getServices(cloneDeep(this.servicesItems))
      vehicle.insurances = this.getInsurances(cloneDeep(this.insuranceItems))
      vehicle.type = this.getType(cloneDeep(vehicle.type))
      vehicle.ownership = this.getOwnership(cloneDeep(vehicle.ownership))
      vehicle.extraEquipment = this.getExtraEquipment(cloneDeep(vehicle.extraEquipment))
      vehicle.registry = this.getRegistry(cloneDeep(vehicle.registry))
      vehicle.plateNumber = vehicle.plateNumber.toUpperCase()

      if (isEmpty(vehicle.measures)) vehicle.measures = []
      if (isEmpty(vehicle.services)) vehicle.services = []
      if (isEmpty(vehicle.type)) vehicle.type = {}
      if (isEmpty(vehicle.ownership)) vehicle.ownership = {}
      if (isEmpty(vehicle.extraEquipment)) vehicle.extraEquipment = {}
      if (isEmpty(vehicle.registry)) vehicle.registry = {}

      if (!vehicle.userCompany) vehicle.userCompany = null
      if (!vehicle.state) vehicle.state = null
      if (!vehicle.activitySector) vehicle.activitySector = null
      if (!vehicle.imei) vehicle.imei = null
      if (!vehicle.chassisNumber) vehicle.chassisNumber = null
      if (!vehicle.registrationDate) vehicle.registrationDate = null
      if (vehicle.ownUse !== true && vehicle.ownUse !== false) vehicle.ownUse = true
      if (!vehicle.disablingDate) vehicle.disablingDate = null
      if (!vehicle.kmCost) vehicle.kmCost = null
      else vehicle.kmCost = parseFloat(vehicle.kmCost)
      if (!vehicle.avgConsumption) vehicle.avgConsumption = null
      else vehicle.avgConsumption = parseFloat(vehicle.avgConsumption)
      if (!vehicle.estimatedKm) vehicle.estimatedKm = null
      else vehicle.estimatedKm = parseInt(vehicle.estimatedKm)
      if (!vehicle.description) vehicle.description = null

      return vehicle
    },
    getMeasures (measures, editedMeasure) {
      const newMeasures = []
      if (measures && measures.length !== 0) {
        measures.forEach((measure) => {
          if (measure && measure.date && (measure.km || measure.km === 0)) {
            newMeasures.push({ km: parseInt(measure.km), date: measure.date })
          }
        })
      }
      if (editedMeasure && editedMeasure.km && editedMeasure.date) {
        const lastMeasure = last(measures)
        if (!lastMeasure ||
          (parseInt(editedMeasure.km) !== parseInt(lastMeasure.km) ||
            editedMeasure.date !== lastMeasure.date)) {
          newMeasures.push({ km: parseInt(editedMeasure.km), date: editedMeasure.date })
        }
      }
      return newMeasures
    },
    getType (type) {
      const editedType = type
      if (!type.equipment) delete type.equipment
      if (!type.model) delete type.model
      if (!type.brand) delete type.brand
      if (!type.classification) delete type.classification
      if (!type.avgConsumption) delete type.avgConsumption
      else type.avgConsumption = parseInt(type.avgConsumption)
      if (!type.kw) delete type.kw
      else type.kw = parseInt(type.kw)
      if (!type.engineDisplacement) delete type.engineDisplacement
      else type.engineDisplacement = parseInt(type.engineDisplacement)
      if (!type.length) delete type.length
      else type.length = parseInt(type.length)
      if (!type.width) delete type.width
      else type.width = parseInt(type.width)
      if (!type.payload) delete type.payload
      else type.payload = parseInt(type.payload)
      if (!type.fuelType) delete type.fuelType
      if (!type.emissionStandard) delete type.emissionStandard
      if (!type.traction) type.traction = '0'
      if (!type.seats) delete type.seats
      else type.seats = parseInt(type.seats)
      return editedType
    },
    getOwnership (ownership) {
      const editedOwnership = ownership
      const rental = ownership.rental
      const purchase = ownership.purchase
      const leasing = ownership.leasing
      const owner = this.editedOwnership.owner
      if (!isEmpty(rental) && rental.startDate && rental.endDate) {
        editedOwnership.rental = {
          company: rental.company || null,
          startDate: rental.startDate,
          endDate: rental.endDate,
          price: parseInt(rental.price) || null,
          redemptionPrice: parseInt(rental.redemptionPrice) || null,
          relief: parseInt(rental.relief) || null,
          kmYear: parseInt(rental.kmYear) || null,
          maxKm: parseInt(rental.maxKm) || null,
          loanerVehicle: rental.loanerVehicle || null,
          towTruck: rental.towTruck || null,
          type: this.ownershipTypeSelected
        }
      } else delete editedOwnership.rental
      if (!isEmpty(purchase) && purchase.date) {
        editedOwnership.purchase = {
          date: purchase.date,
          km: parseInt(purchase.km) || null,
          price: parseInt(purchase.price) || null
        }
      } else delete editedOwnership.purchase
      if (!isEmpty(leasing) && leasing.startDate && leasing.endDate) {
        editedOwnership.leasing = {
          startDate: leasing.startDate,
          endDate: leasing.endDate,
          redemptionPrice: parseInt(leasing.redemptionPrice) || null
        }
      } else delete editedOwnership.leasing
      if (!owner) delete ownership.owner
      else ownership.owner = owner
      return editedOwnership
    },
    getExtraEquipment (extraEquipment) {
      const editedExtraEquipment = extraEquipment
      if (!editedExtraEquipment.key) delete editedExtraEquipment.key
      else editedExtraEquipment.key = parseInt(editedExtraEquipment.key)
      return editedExtraEquipment
    },
    getRegistry (registry) {
      const editedRegistry = registry
      const alboCodes = this.alboCodesSelected
      if (alboCodes && alboCodes.length === 0) delete editedRegistry.codes
      else editedRegistry.codes = this.getAlboCodes(this.alboCodesSelected)
      if (!editedRegistry.registrationDate) delete editedRegistry.registrationDate
      return editedRegistry
    },
    getServices (services) {
      const editedServices = []
      services.forEach((service) => {
        editedServices.push({
          type: service.type || null,
          note: service.note || null,
          expDate: service.expDate || null,
          pin: service.pin || null,
          number: service.number || null
        })
      })
      return editedServices
    },
    getInsurances (insurances) {
      const editedInsurances = []
      insurances.forEach((insurance) => {
        editedInsurances.push({
          company: insurance.company,
          number: insurance.number,
          startDate: insurance.startDate,
          endDate: insurance.endDate,
          price: insurance.price ? parseFloat(insurance.price) : null,
          installment: insurance.installment ? parseFloat(insurance.installment) : null,
          paymentFreq: insurance.paymentFreq ? parseFloat(insurance.paymentFreq) : null
        })
      })
      return editedInsurances
    },
    getAlboCodes (items) {
      const codes = []
      items.forEach((item) => {
        codes.push(item.value)
      })
      return codes
    },
    getCurrentTab (tabName) {
      let foundedTab = first(this.tabs)
      if (tabName) {
        foundedTab = find(this.tabs, obj => obj.name === tabName)
      } else {
        foundedTab = find(this.tabs, obj => obj.index === this.currentTab)
      }
      return foundedTab
    },
    registrationDateChanged (DateObj, val) {
      const registration = val || null
      const rentalStart = this.mainBody.ownership.rental.startDate || null
      const rentalEnd = this.mainBody.ownership.rental.endDate || null
      const leasingStart = this.mainBody.ownership.leasing.startDate || null
      const leasingEnd = this.mainBody.ownership.leasing.endDate || null
      const purchaseDate = this.mainBody.ownership.purchase.date || null

      const measures = this.mainBody.measures
      if (val && measures && measures.length !== 0 && DateTime.fromSQL(val) > DateTime.fromSQL(last(measures).date)) {
        last(this.mainBody.measures).date = ''
      }
      this.disablingDateConfig.minDate = val || null

      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    rentalOptionDateStartChanged (DateObj, val) {
      const registration = this.mainBody.registrationDate || null
      const rentalStart = val || null
      const rentalEnd = this.mainBody.ownership.rental.endDate || null
      const leasingStart = this.mainBody.ownership.leasing.startDate || null
      const leasingEnd = this.mainBody.ownership.leasing.endDate || null
      const purchaseDate = this.mainBody.ownership.purchase.date || null
      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    rentalOptionDateEndChanged (DateObj, val) {
      const registration = this.mainBody.registrationDate || null
      const rentalStart = this.mainBody.ownership.rental.startDate || null
      const rentalEnd = val || null
      const leasingStart = this.mainBody.ownership.leasing.startDate || null
      const leasingEnd = this.mainBody.ownership.leasing.endDate || null
      const purchaseDate = this.mainBody.ownership.purchase.date || null
      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    leasingOptionDateStartChanged (DateObj, val) {
      const registration = this.mainBody.registrationDate || null
      const rentalStart = this.mainBody.ownership.rental.startDate || null
      const rentalEnd = this.mainBody.ownership.rental.endDate || null
      const leasingStart = val || null
      const leasingEnd = this.mainBody.ownership.leasing.endDate || null
      const purchaseDate = this.mainBody.ownership.purchase.date || null
      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    leasingOptionDateEndChanged (DateObj, val) {
      const registration = this.mainBody.registrationDate || null
      const rentalStart = this.mainBody.ownership.rental.startDate || null
      const rentalEnd = this.mainBody.ownership.rental.endDate || null
      const leasingStart = this.mainBody.ownership.leasing.startDate || null
      const leasingEnd = val || null
      const purchaseDate = this.mainBody.ownership.purchase.date || null
      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    purchaseOptionDateStartChanged (DateObj, val) {
      const registration = this.mainBody.registrationDate || null
      const rentalStart = this.mainBody.ownership.rental.startDate || null
      const rentalEnd = this.mainBody.ownership.rental.endDate || null
      const leasingStart = this.mainBody.ownership.leasing.startDate || null
      const leasingEnd = this.mainBody.ownership.leasing.endDate || null
      const purchaseDate = val || null
      this.updateDisabledRange(rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration)
    },
    insuranceStartDateOptionChanged (DateObj, val) {
      const registrationDate = this.mainBody.registrationDate
      const insurenceStart = val || null
      const insuranceEnd = this.selectedInsurance.endDate || null

      this.insuranceStartDateOptions.minDate = registrationDate
      this.insuranceEndDateOptions.minDate = insurenceStart || registrationDate
      this.insuranceStartDateOptions.maxDate = insuranceEnd
    },
    insuranceEndDateOptionChanged (DateObj, val) {
      const registrationDate = this.mainBody.registrationDate
      const insurenceStart = this.selectedInsurance.startDate || null
      const insuranceEnd = val || null

      this.insuranceStartDateOptions.minDate = registrationDate
      this.insuranceEndDateOptions.minDate = insurenceStart || registrationDate
      this.insuranceStartDateOptions.maxDate = insuranceEnd
    },
    updateDisabledRange (rentalStart, rentalEnd, leasingStart, leasingEnd, purchaseDate, registration) {
      const today = DateTime.local().toSQLDate()
      const dateTimeToday = DateTime.local()
      const dateTimeRentalEnd = DateTime.fromSQL(rentalEnd)
      const dateTimeLeasingEnd = DateTime.fromSQL(leasingEnd)

      const leasingStartLessDay = leasingStart ? DateTime.fromSQL(leasingStart).plus({ days: -1 }).toSQLDate() : leasingStart
      const leasingEndPlusDay = leasingEnd ? DateTime.fromSQL(leasingEnd).plus({ days: 1 }).toSQLDate() : leasingEnd
      const rentalStartLessDay = rentalStart ? DateTime.fromSQL(rentalStart).plus({ days: -1 }).toSQLDate() : rentalStart
      const rentalEndPlusDay = rentalEnd ? DateTime.fromSQL(rentalEnd).plus({ days: 1 }).toSQLDate() : rentalEnd
      const purchaseDateLessDay = purchaseDate ? DateTime.fromSQL(purchaseDate).plus({ days: -1 }).toSQLDate() : purchaseDate

      this.alboRegistrationOption.minDate = registration
      this.lastMeasureOption.minDate = (this.lastMeasure && this.lastMeasure.date) || registration
      this.serviceSelectedOption.minDate = registration
      this.disablingDateConfig.minDate = registration
      this.rentalOptionDateStart.minDate = registration
      this.rentalOptionDateEnd.minDate = rentalStart || registration
      this.leasingOptionDateStart.minDate = registration
      this.leasingOptionDateEnd.minDate = leasingStart || registration
      this.purchaseOptionDateStart.minDate =
        rentalEnd && leasingEnd ? (DateTime.fromSQL(leasingEndPlusDay) > DateTime.fromSQL(rentalEndPlusDay) ? leasingEndPlusDay : rentalEndPlusDay)
          : (rentalEndPlusDay || leasingEndPlusDay || registration)

      this.rentalOptionDateStart.maxDate = rentalEnd ? (dateTimeRentalEnd > dateTimeToday ? today : rentalEnd) : (purchaseDateLessDay || today)
      this.leasingOptionDateStart.maxDate = leasingEnd ? (dateTimeLeasingEnd > dateTimeToday ? today : leasingEnd) : (purchaseDateLessDay || today)
      this.purchaseOptionDateStart.maxDate = today
      this.lastMeasureOption.maxDate = today
      this.rentalOptionDateEnd.maxDate = purchaseDateLessDay
      this.leasingOptionDateEnd.maxDate = purchaseDateLessDay
      this.optionRegistrationDate.maxDate =
        rentalStart && leasingStart
          ? (DateTime.fromSQL(rentalStart) < DateTime.fromSQL(leasingStart) ? rentalStart : leasingStart)
          : (rentalStart || leasingStart || purchaseDate || today)

      this.leasingOptionDateStart.disable = [(dateObj) => {
        if (rentalStart && rentalEnd && leasingEnd) {
          return (dateObj < new Date(rentalEnd) && new Date(leasingEnd) > new Date(rentalEnd))
        } else if (rentalStart && rentalEnd) {
          return (dateObj > new Date(rentalStartLessDay) && dateObj < new Date(rentalEnd))
        }
        return false
      }]
      this.leasingOptionDateEnd.disable = [(dateObj) => {
        if (rentalStart && rentalEnd && leasingStart) {
          return (dateObj >= new Date(rentalStartLessDay) && new Date(leasingStart) <= new Date(rentalStart))
        } else if (rentalStart && rentalEnd) {
          return (dateObj >= new Date(rentalStartLessDay) && dateObj <= new Date(rentalEnd))
        }
        return false
      }]

      this.rentalOptionDateStart.disable = [(dateObj) => {
        if (leasingStart && leasingEnd && rentalEnd) {
          return (dateObj < new Date(leasingEnd) && new Date(rentalEnd) > new Date(leasingEnd))
        } else if (leasingStart && leasingEnd) {
          return (dateObj > new Date(leasingStartLessDay) && dateObj < new Date(leasingEnd))
        }
        return false
      }]
      this.rentalOptionDateEnd.disable = [(dateObj) => {
        if (leasingStart && leasingEnd && rentalStart) {
          return (dateObj >= new Date(leasingStartLessDay) && new Date(rentalStart) <= new Date(leasingStartLessDay))
        } else if (leasingStart && leasingEnd) {
          return (dateObj >= new Date(leasingStartLessDay) && dateObj <= new Date(leasingEndPlusDay))
        }
        return false
      }]
      this.purchaseOptionDateStart.disable = [(dateObj) => {
        if (rentalEnd && leasingEnd) {
          if (new Date(rentalEnd) > new Date(leasingEnd)) return dateObj < new Date(rentalEnd)
          else return dateObj < new Date(leasingEnd)
        } else if (rentalEnd) return dateObj < new Date(rentalEnd)
        else if (leasingEnd) return dateObj < new Date(leasingEnd)
        return false
      }]

      this.dateErrors = (!leasingStart && !!leasingEnd) || (!!leasingStart && !leasingEnd) || (!rentalStart && !!rentalEnd) || (!!rentalStart && !rentalEnd)

      this.insuranceStartDateOptionChanged(null, this.selectedInsurance.startDate)
      this.insuranceEndDateOptionChanged(null, this.selectedInsurance.endDate)
    },
    checkDateValidation (val) {
      if (val === null) {
        return { class: 'form-control', isError: null }
      } else if (val) {
        return { class: 'form-control is-valid', isError: false }
      } else {
        return { class: 'form-control is-invalid', isError: true }
      }
    },
    logData (vehicleId, put) {
      const logObj = Vue.getLogObj(this, put ? 'put' : 'post', '/organization/registry_vehicles/:id')

      logObj.data.details = []

      if (vehicleId) logObj.data.details.push({text: 'message.vehicle', option: 1, value: vehicleId, key: logKeys.vehicle})

      Vue.myLogPost(logObj)
    },
    isNumber (evt, input, integerValue) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))){
        if((!integerValue && (charCode !== 46 || input.indexOf('.') !== -1)) || integerValue) {
          evt.preventDefault();
        }
      } else {
        return true;
      }
    }
  }
}
</script>

<style scoped>
.slide-left-enter-active {
  transition: all 2s ease;
}
.slide-left-enter, .slide-left-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-enter-active {
  transition: all 2s ease;
}
.slide-right-enter, .slide-right-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.scroll-fade-enter-active {
  transition: all 1s ease;
}
.scroll-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.scroll-fade-enter, .scroll-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.card-body {
  padding: 0 !important;
}

.max-height-200-px {
  max-height: 200px;
  overflow-y: auto;
}
.modal-body {
  word-wrap: break-word;
  padding: 0.2rem;
}
.service-container{
  padding: 2rem;
}
</style>
